import { useEffect, useLayoutEffect, useState, useContext, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { db, auth } from '../firebase';
import { fetchPublicProfiles } from '../utils/publicProfilesBatch';
import { collection, query, where, limit, getDocs, doc, getDoc, orderBy, startAfter, documentId, addDoc, serverTimestamp } from 'firebase/firestore';
import { UserContext } from '../contexts/UserContext';
import { useContact } from '../contexts/ContactContext';
import { useRegisterPullToRefresh } from '../hooks/useRegisterPullToRefresh';
import ActiveTraderBadge from '../components/ActiveTraderBadge';
import SEO from '../components/SEO';
import PedalPriceDisplay from '../components/PedalPriceDisplay';
import PriceDealThumbnailBadge from '../components/PriceDealThumbnailBadge';
import ListingThumb from '../components/ListingThumb';
import WantDemandBadge from '../components/WantDemandBadge';
import { useMyWants } from '../hooks/useMyWants';
import { runWithTimeoutAndRetry, armRecoveryOnReconnect, throwIfOfflineEmpty } from '../utils/recoverableLoad';
import { isTransientConnectivityError } from '../utils/transientReadRetry';
import { listingListedAtMs } from '../utils/sortPedalsByListedAt';
import { reloadPage } from '../utils/reloadPage';
import UserSearchSuggestions from '../components/UserSearchSuggestions';
import { EFFECT_TYPES } from '../utils/effectTypes';
import { listingImageUrls, isPlaceholderListing, placeholderFor, SKETCH_PLACEHOLDER_URL } from '../utils/listingPlaceholder';
import { getMySaleOnlyPedalIds } from '../utils/dashboardMatchCounters';
import { allowsTrade, OFFER_FILTER_OPTIONS, matchesOfferFilter, offerModeOf } from '../utils/offerMode';
import { shouldReloadBrowse, canAppendBrowse, shouldPauseScan } from '../utils/browseReloadDecision';

// Single-attempt timeout for the user-facing pedal loaders. On failure we
// silently retry once after RETRY_DELAY_MS (see loadFeaturedPedals catch).
// If the retry also fails, the finally block clears the skeleton state and
// the page falls through to its existing empty-state UI — no error UI is
// shown to the user.
const FETCH_TIMEOUT_MS = 10_000;
// Per-load scan budget (see shouldPauseScan): a load stops after this many
// 50-listing batches or this much time and reports has-more, instead of
// scanning the whole marketplace for a rare search term inside one load.
const MAX_BATCHES_PER_LOAD = 8;
const SCAN_BUDGET_MS = 6000;
const RETRY_DELAY_MS = 1_000;

// Bump the key suffix whenever the saved pedal shape gets a new required field
// — old entries are silently ignored. v2 added `owner.membershipTier` and
// `ownerPhotoURL` so the card can render the owner lockup (photo + tier badge
// + trade-count badge); restoring a v1 entry would render a card with no
// badges and no profile image.
const STORAGE_KEY = 'latestPedals_scrollState_v2';

// Infinite scroll config
const MAX_AUTO_LOADS = 8; // auto-loads ~8 batches (~224 pedals) before requiring manual click
const SENTINEL_ROOT_MARGIN = '1500px 0px'; // start fetching when sentinel is ~1-1.5 viewport heights below the bottom edge
const SUPPORTS_INTERSECTION_OBSERVER =
  typeof window !== 'undefined' && 'IntersectionObserver' in window;

const readSavedState = (locationState) => {
  if (locationState?.fresh) return null;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const state = JSON.parse(raw);
    if (Date.now() - state.timestamp > 30 * 60 * 1000) return null;
    return state;
  } catch (e) {
    return null;
  }
};

const LatestPedals = () => {
  const { firestoreUser } = useContext(UserContext);
  const { openContact } = useContact();
  const location = useLocation();
  const navigate = useNavigate();

  const savedOnMount = useRef(readSavedState(location.state));
  const s = savedOnMount.current;

  const isRestoringState = useRef(!!s);
  const hasRestoredState = useRef(!!s);
  const prevSearchQueryRef = useRef(s?.searchQuery || '');
  const prevFilterTypeRef = useRef(s?.filterType || 'all');
  const prevCategoryFilterRef = useRef(s?.categoryFilter || 'all');
  const prevSearchUserWantsRef = useRef(s?.searchUserWants || false);
  const prevFilterPriceRef = useRef(s?.filterPrice || { min: '', max: '' });
  const prevFilterEffectTypesRef = useRef(s?.filterEffectTypes || []);
  const prevFilterConditionsRef = useRef(s?.filterConditions || []);
  const prevFilterDecadesRef = useRef(s?.filterDecades || []);
  const prevFilterOffersRef = useRef(Array.isArray(s?.filterOffers) ? s.filterOffers : []);
  const lastProcessedLocationKey = useRef(null);
  const [featuredPedals, setFeaturedPedals] = useState(s?.pedals || []);
  const [isLoadingFeatured, setIsLoadingFeatured] = useState(!s);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  // Phase 2a (2026-09-29): a load whose retry ALSO failed, while still the
  // current fetch. Drives the honest "Couldn't load pedals" state instead of
  // the marketplace-empty message, and arms one-shot reconnect recovery.
  // Cleared whenever any pedal load starts. Only consulted by the render
  // when there are zero pedals — a failed APPEND never hides loaded cards.
  const [browseLoadFailed, setBrowseLoadFailed] = useState(false);
  // The last load stopped at its scan budget with fewer than a full page: more may match further on.
  const [scanPaused, setScanPaused] = useState(false);
  const [filterType, setFilterType] = useState(s?.filterType || 'all');
  const [categoryFilter, setCategoryFilter] = useState(s?.categoryFilter || 'all');
  const [userWantedPedals, setUserWantedPedals] = useState([]);
  // Seeded from the state's own reference, not a fresh literal: `[] !== []`,
  // and a mismatch here would make the first effect run look like a wants
  // change. (The filter refs above DO seed from fresh literals, which is why
  // a fresh mount counts as "user initiated" today; left alone on purpose.)
  const prevUserWantedPedalsRef = useRef(userWantedPedals);
  const [showBackToTop, setShowBackToTop] = useState(false);
  /* Desktop-only sticky header compaction: when the user scrolls past the
     threshold, the h1 + subtitle fade out and the search/filter row condenses
     into a slim bar anchored to the top of the viewport. Mobile uses
     showMobileChromeBar + scroll-direction logic instead (see scroll handler). */
  const [isHeaderCompact, setIsHeaderCompact] = useState(() => {
    if (typeof window === 'undefined') return false;
    if (window.innerWidth <= 768) return false;
    return window.scrollY > 400;
  });
  /* Mobile fixed chrome overlay — only on scroll-up after the in-flow bar has
     scrolled off-screen. The in-flow bar (below) stays in document flow. */
  const [showMobileChromeBar, setShowMobileChromeBar] = useState(false);
  /* Reserved slot height — always held in layout so listings never shift. */
  const [mobileChromeBarHeight, setMobileChromeBarHeight] = useState(0);
  const mobileChromeInflowRef = useRef(null);
  const mobileChromeSlotRef = useRef(null);
  const headerExpandedRef = useRef(null);
  const wasHeaderCompactRef = useRef(false);
  const mobileChromeBarHeightRef = useRef(0);
  const navbarHeightRef = useRef(125);
  const lastScrollYRef = useRef(typeof window !== 'undefined' ? window.scrollY : 0);
  /* Prevents the fixed bar re-appearing during iOS rubber-band once handed
     off to the in-flow bar in the chrome slot. */
  const mobileChromeHandedOffRef = useRef(false);
  const [searchInput, setSearchInput] = useState(s?.searchQuery || '');
  const [searchQuery, setSearchQuery] = useState(s?.searchQuery || '');
  const [searchUserWants, setSearchUserWants] = useState(s?.searchUserWants || false);
  const [filterPrice, setFilterPrice] = useState(s?.filterPrice || { min: '', max: '' });
  const [filterEffectTypes, setFilterEffectTypes] = useState(s?.filterEffectTypes || []);
  const [filterConditions, setFilterConditions] = useState(s?.filterConditions || []);
  const [filterDecades, setFilterDecades] = useState(s?.filterDecades || []);
  const [filterOffers, setFilterOffers] = useState(Array.isArray(s?.filterOffers) ? s.filterOffers : []); // 'Trade' / 'Buy' (plan §5)
  const [isFilterSheetOpen, setIsFilterSheetOpen] = useState(false);
  const [isFilterSheetClosing, setIsFilterSheetClosing] = useState(false);
  const [filterSubPage, setFilterSubPage] = useState(null);
  const FILTER_SHEET_ANIM_MS = 220;
  const [lastVisible, setLastVisible] = useState(null);
  const [hasMore, setHasMore] = useState(s?.hasMore ?? true);
  const [userDataCache, setUserDataCache] = useState({});
  const [potentialMatchUserIds, setPotentialMatchUserIds] = useState(new Set());
  const [autoLoadCount, setAutoLoadCount] = useState(0);
  // Tracks which compute path produced the user's current suggestions feed:
  // 'personalized' (bridge-want overlap with other users) or 'popular_fallback'
  // (user has no signal — surface platform-wide popular wants instead). Drives
  // the explainer header at the top of the Suggested grid.
  const [suggestedSource, setSuggestedSource] = useState(null);
  const sentinelRef = useRef(null);
  // Concurrency control for loadFeaturedPedals.
  //   fetchInFlightRef: blocks concurrent *append* calls from racing each other.
  //   fetchTokenRef:    monotonic counter; only the latest in-flight fetch is
  //                     allowed to apply its results / clear the in-flight flag.
  //                     Older fetches are silently discarded. This is essential
  //                     because StrictMode (dev) and rapid filter changes can
  //                     cause overlapping resets that would otherwise clobber
  //                     each other's state.
  const fetchInFlightRef = useRef(false);
  const fetchTokenRef = useRef(0);
  const user = auth.currentUser;
  // One want-list listener for every heart badge on the page (never per card).
  const { wants: myWants } = useMyWants(user);

  // Intentional navigation from dashboard (state.fresh) should land at page top; scroll before paint so SPA doesn't keep dashboard scroll
  useLayoutEffect(() => {
    if (location.state?.fresh) {
      window.scrollTo(0, 0);
      setShowMobileChromeBar(false);
      mobileChromeHandedOffRef.current = false;
      lastScrollYRef.current = 0;
    }
  }, [location.key, location.state?.fresh]);

  // Flexible matching function - matches specific make/model OR effect types
  const doesPedalMatchWant = (want, pedal) => {
    const hasMake = want.make && want.make.trim() !== '';
    const hasModel = want.model && want.model.trim() !== '';
    const hasEffectTypes = want.effectTypes && want.effectTypes.length > 0;

    if (want.decade && want.decade.trim() !== '') {
      if (!pedal.decade || pedal.decade !== want.decade) return false;
    }

    if (hasMake && hasModel) {
      const makeMatch = want.make.toLowerCase() === pedal.make?.toLowerCase();
      const modelMatch = want.model.toLowerCase() === pedal.model?.toLowerCase();
      if (!makeMatch || !modelMatch) return false;

      if (hasEffectTypes) {
        if (!pedal.effectTypes || pedal.effectTypes.length === 0) return false;
        if (!want.effectTypes.some(wt => pedal.effectTypes.includes(wt))) return false;
      }
      return true;
    }

    if (hasMake && !hasModel) {
      const makeMatch = want.make.toLowerCase() === pedal.make?.toLowerCase();
      if (!makeMatch) return false;

      if (hasEffectTypes) {
        if (!pedal.effectTypes || pedal.effectTypes.length === 0) return false;
        if (!want.effectTypes.some(wt => pedal.effectTypes.includes(wt))) return false;
      }
      return true;
    }

    if (hasEffectTypes) {
      if (!pedal.effectTypes || pedal.effectTypes.length === 0) return false;
      return want.effectTypes.some(wt => pedal.effectTypes.includes(wt));
    }
    
    return false;
  };

  const closeFilterSheet = () => {
    if (!isFilterSheetOpen || isFilterSheetClosing) return;
    setIsFilterSheetClosing(true);
    setTimeout(() => {
      setIsFilterSheetOpen(false);
      setIsFilterSheetClosing(false);
      setFilterSubPage(null);
    }, FILTER_SHEET_ANIM_MS);
  };

  const openFilterSheet = () => {
    setIsFilterSheetClosing(false);
    setIsFilterSheetOpen(true);
    setFilterSubPage(null);
  };

  useEffect(() => {
    const handleAddOpened = () => {
      if (isFilterSheetOpen) closeFilterSheet();
    };
    window.addEventListener('bottomTabBarAddOpened', handleAddOpened);
    return () => window.removeEventListener('bottomTabBarAddOpened', handleAddOpened);
  }, [isFilterSheetOpen]);

  const activeFilterCount =
    (filterType !== 'all' ? 1 : 0) +
    (searchUserWants ? 1 : 0) +
    (categoryFilter !== 'all' ? 1 : 0) +
    (filterEffectTypes.length > 0 ? 1 : 0) +
    (filterConditions.length > 0 ? 1 : 0) +
    (filterDecades.length > 0 ? 1 : 0) +
    (filterOffers.length > 0 ? 1 : 0) +
    ((filterPrice.min !== '' || filterPrice.max !== '') ? 1 : 0);

  const clearAllFilters = () => {
    setFilterType('all');
    setCategoryFilter('all');
    setSearchUserWants(false);
    setFilterPrice({ min: '', max: '' });
    setFilterEffectTypes([]);
    setFilterConditions([]);
    setFilterDecades([]);
    setFilterOffers([]);
  };

  const applyAdvancedFilters = (pedals) => {
    let result = pedals;
    if (filterPrice.min !== '') {
      const min = Number(filterPrice.min);
      result = result.filter(p => p.price >= min);
    }
    if (filterPrice.max !== '') {
      const max = Number(filterPrice.max);
      result = result.filter(p => p.price <= max);
    }
    if (filterEffectTypes.length > 0) {
      result = result.filter(p =>
        p.effectTypes && p.effectTypes.some(t => filterEffectTypes.includes(t))
      );
    }
    if (filterConditions.length > 0) {
      result = result.filter(p => filterConditions.includes(p.condition));
    }
    if (filterDecades.length > 0) {
      result = result.filter(p => filterDecades.includes(p.decade));
    }
    if (filterOffers.length > 0) {
      result = result.filter(p => matchesOfferFilter(p, filterOffers));
    }
    return result;
  };

  // Save state to sessionStorage before navigating away
  const saveScrollState = () => {
    const state = {
      pedals: featuredPedals,
      scrollY: window.scrollY,
      filterType,
      categoryFilter,
      searchQuery,
      searchUserWants,
      filterPrice,
      filterEffectTypes,
      filterConditions,
      filterDecades,
      filterOffers,
      hasMore,
      timestamp: Date.now()
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  };

  // Handle mount: async operations for restored state, or fresh navigation reset
  useEffect(() => {
    if (lastProcessedLocationKey.current === location.key) return;
    // Track whether this is the very first mount so we don't double-load
    // alongside the filter-deps effect that always fires on initial mount.
    const isInitialMount = lastProcessedLocationKey.current === null;
    lastProcessedLocationKey.current = location.key;

    if (location.state?.fresh) {
      sessionStorage.removeItem(STORAGE_KEY);
      isRestoringState.current = false;
      hasRestoredState.current = false;
      savedOnMount.current = null;

      // If filter/search values are already at their defaults, the setState
      // calls below are no-ops and the [filter,...] effect will NOT re-fire,
      // which leaves isLoadingFeatured stuck true forever (the "white boxes"
      // bug when re-tapping the Latest tab while already on this page).
      // Detect that case so we can trigger the fetch ourselves.
      const filterValuesAlreadyDefault =
        filterType === 'all' &&
        categoryFilter === 'all' &&
        searchQuery === '' &&
        searchUserWants === false;

      setFilterType('all');
      setCategoryFilter('all');
      setSearchInput('');
      setSearchQuery('');
      setSearchUserWants(false);
      setFilterPrice({ min: '', max: '' });
      setFilterEffectTypes([]);
      setFilterConditions([]);
      setFilterDecades([]);
      setFeaturedPedals([]);
      setLastVisible(null);
      setHasMore(true);
      setIsLoadingFeatured(true);
      window.history.replaceState({}, '', location.pathname);
      window.scrollTo(0, 0);
      setShowMobileChromeBar(false);
      mobileChromeHandedOffRef.current = false;
      lastScrollYRef.current = 0;
      loadUserWantedPedals();
      loadPotentialMatches();

      if (!isInitialMount && filterValuesAlreadyDefault) {
        loadFeaturedPedals(true);
      }
      return;
    }

    if (savedOnMount.current) {
      const scrollY = savedOnMount.current.scrollY;
      const pedals = savedOnMount.current.pedals;
      savedOnMount.current = null;

      if (pedals?.length > 0) {
        getDoc(doc(db, 'listings', pedals[pedals.length - 1].id))
          .then(lastDoc => { if (lastDoc.exists()) setLastVisible(lastDoc); })
          .catch(() => {});
      }

      loadUserWantedPedals();
      loadPotentialMatches();

      setTimeout(() => {
        window.scrollTo(0, scrollY || 0);
        // Sync compact-header state to the restored scroll position. The
        // scroll listener only fires on real scroll events, so without this
        // explicit set the compact bar would stay hidden until the user
        // scrolled again — even if the restored position is past the threshold.
        const restoredY = scrollY || 0;
        const isMobile = window.innerWidth <= 768;
        if (isMobile) {
          setShowMobileChromeBar(false);
          const chromeSlotBottom = navbarHeightRef.current + mobileChromeBarHeightRef.current;
          mobileChromeHandedOffRef.current = restoredY <= chromeSlotBottom;
          lastScrollYRef.current = restoredY;
        } else {
          setIsHeaderCompact(restoredY > 400);
        }
        setShowBackToTop(restoredY > 300);
        setTimeout(() => { isRestoringState.current = false; }, 200);
      }, 100);

      return;
    }

    loadUserWantedPedals();
    loadPotentialMatches();
  }, [location.key, location.state]);

  // Clear stale saved state when filters change, then auto-save once new data loads
  useEffect(() => {
    if (!isRestoringState.current) {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  }, [filterType, categoryFilter, searchQuery, searchUserWants, filterPrice, filterEffectTypes, filterConditions, filterDecades, filterOffers]);

  // Auto-save state to sessionStorage once data and filters have settled
  useEffect(() => {
    if (isRestoringState.current || isLoadingFeatured) return;
    if (featuredPedals.length === 0) return;

    const state = {
      pedals: featuredPedals,
      scrollY: window.scrollY,
      filterType,
      categoryFilter,
      searchQuery,
      searchUserWants,
      filterPrice,
      filterEffectTypes,
      filterConditions,
      filterDecades,
      filterOffers,
      hasMore,
      timestamp: Date.now()
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [featuredPedals, filterType, categoryFilter, searchQuery, searchUserWants, filterPrice, filterEffectTypes, filterConditions, filterDecades, hasMore, isLoadingFeatured]);

  // Save full state on beforeunload (captures latest scroll position before unexpected reloads)
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (featuredPedals.length > 0) {
        const state = {
          pedals: featuredPedals,
          scrollY: window.scrollY,
          filterType,
          categoryFilter,
          searchQuery,
          searchUserWants,
          filterPrice,
          filterEffectTypes,
          filterConditions,
          filterDecades,
          filterOffers,
          hasMore,
          timestamp: Date.now()
        };
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [featuredPedals, filterType, categoryFilter, searchQuery, searchUserWants, filterPrice, filterEffectTypes, filterConditions, filterDecades, hasMore]);

  // Clear saved state when navigating away from this page
  useEffect(() => {
    return () => {
      const isGoingToListing = window.location.pathname.startsWith('/listing/');
      if (!location.pathname.includes('/latest-pedals') && !isGoingToListing) {
        sessionStorage.removeItem(STORAGE_KEY);
      }
    };
  }, [location]);

  useEffect(() => {
    // Track what actually changed so we can distinguish user-initiated changes
    // (search/filter) from background changes (userWantedPedals loading)
    const searchQueryChanged = prevSearchQueryRef.current !== searchQuery;
    const filterTypeChanged = prevFilterTypeRef.current !== filterType;
    const categoryFilterChanged = prevCategoryFilterRef.current !== categoryFilter;
    const searchUserWantsChanged = prevSearchUserWantsRef.current !== searchUserWants;
    const filterPriceChanged = prevFilterPriceRef.current !== filterPrice;
    const filterEffectTypesChanged = prevFilterEffectTypesRef.current !== filterEffectTypes;
    const filterConditionsChanged = prevFilterConditionsRef.current !== filterConditions;
    const filterDecadesChanged = prevFilterDecadesRef.current !== filterDecades;
    const filterOffersChanged = prevFilterOffersRef.current !== filterOffers;
    const userWantedPedalsChanged = prevUserWantedPedalsRef.current !== userWantedPedals;

    prevSearchQueryRef.current = searchQuery;
    prevFilterTypeRef.current = filterType;
    prevCategoryFilterRef.current = categoryFilter;
    prevSearchUserWantsRef.current = searchUserWants;
    prevFilterPriceRef.current = filterPrice;
    prevFilterEffectTypesRef.current = filterEffectTypes;
    prevFilterConditionsRef.current = filterConditions;
    prevFilterDecadesRef.current = filterDecades;
    prevFilterOffersRef.current = filterOffers;
    prevUserWantedPedalsRef.current = userWantedPedals;
    
    if (isRestoringState.current) {
      return;
    }
    
    const userInitiatedChange = searchQueryChanged || filterTypeChanged || categoryFilterChanged || searchUserWantsChanged || filterPriceChanged || filterEffectTypesChanged || filterConditionsChanged || filterDecadesChanged || filterOffersChanged;
    
    if (hasRestoredState.current && !userInitiatedChange) {
      hasRestoredState.current = false;
      return;
    }
    
    hasRestoredState.current = false;

    // Wants arriving after mount (or after auth resolves) change nothing on
    // the All / Suggested tabs; the Wanted tab still reloads with them. See
    // src/utils/browseReloadDecision.js.
    if (!shouldReloadBrowse({ userInitiatedChange, userWantedPedalsChanged, filterType })) {
      return;
    }
    
    setFeaturedPedals([]);
    setLastVisible(null);
    setHasMore(true);
    setAutoLoadCount(0);
    if (filterType === 'suggested') {
      loadSuggestedListings();
    } else {
      setSuggestedSource(null);
      loadFeaturedPedals(true);
    }
  }, [filterType, categoryFilter, searchQuery, searchUserWants, userWantedPedals, filterPrice, filterEffectTypes, filterConditions, filterDecades, filterOffers]);

  useEffect(() => {
    // Skip if we're restoring from saved state (already loaded in mount effect)
    if (isRestoringState.current) {
      return;
    }
    loadUserWantedPedals();
    loadPotentialMatches();
  }, [user]);

  // Debounce search input → searchQuery (400ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Measure in-flow mobile chrome bar and lock the slot height so listings
  // never shift when the fixed overlay replaces the in-flow content.
  useLayoutEffect(() => {
    const el = mobileChromeInflowRef.current;
    if (!el) return;
    const measure = () => {
      const h = el.offsetHeight;
      if (h > 0) {
        mobileChromeBarHeightRef.current = h;
        setMobileChromeBarHeight(h);
      }
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [searchInput, activeFilterCount]);

  useLayoutEffect(() => {
    const readNavbarHeight = () => {
      const v = getComputedStyle(document.documentElement).getPropertyValue('--navbar-height').trim();
      navbarHeightRef.current = v ? parseFloat(v) : 125;
    };
    readNavbarHeight();
    window.addEventListener('resize', readNavbarHeight);
    return () => window.removeEventListener('resize', readNavbarHeight);
  }, []);

  // WebKit keeps a focused field in view: while the search input still holds
  // focus, re-renders (results arriving, batches appending) scroll the page
  // back to that input — which lives at the top. Chrome doesn't do this, so it
  // reads as a Safari-only "snaps to the top while scrolling" bug. Whenever the
  // header chrome swaps over and the focused field is on its way off-screen,
  // release focus so WebKit has nothing to scroll back to.
  const blurFocusWithin = (container) => {
    const active = document.activeElement;
    if (container && active && container.contains(active) && typeof active.blur === 'function') {
      active.blur();
    }
  };

  // Show/hide "Back to Top" button + header chrome based on scroll.
  // Mobile: scroll-direction reveal; hands off to in-flow bar in the chrome slot.
  // Desktop: threshold-based compact bar (unchanged).
  useEffect(() => {
    const handleScroll = () => {
      const y = window.scrollY;
      setShowBackToTop(y > 300);
      const isMobile = window.innerWidth <= 768;
      if (isMobile) {
        const lastY = lastScrollYRef.current;
        const delta = y - lastY;
        const chromeSlotBottom = navbarHeightRef.current + mobileChromeBarHeightRef.current;
        const pastChromeSlot = y > chromeSlotBottom;
        const slotTop = mobileChromeSlotRef.current?.getBoundingClientRect().top ?? -1;

        if (y > chromeSlotBottom + 30) {
          mobileChromeHandedOffRef.current = false;
        }

        // Once the chrome slot enters the viewport, hand off to the in-flow
        // bar instead of frame-by-frame top updates on the fixed overlay.
        if (slotTop >= 0 || y <= 10) {
          setShowMobileChromeBar(false);
          if (slotTop >= 0) mobileChromeHandedOffRef.current = true;
        } else if (delta > 5) {
          setShowMobileChromeBar(false);
        } else if (delta < -5 && pastChromeSlot && !mobileChromeHandedOffRef.current) {
          blurFocusWithin(mobileChromeInflowRef.current);
          setShowMobileChromeBar(true);
        }
        lastScrollYRef.current = y;
      } else {
        const nextCompact = y > 400;
        if (nextCompact && !wasHeaderCompactRef.current) {
          blurFocusWithin(headerExpandedRef.current);
        }
        wasHeaderCompactRef.current = nextCompact;
        setIsHeaderCompact(nextCompact);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const loadUserWantedPedals = async () => {
    if (!user) {
      return;
    }

    try {
      const q = query(collection(db, 'wantList'), where('uid', '==', user.uid));
      const snapshot = await getDocs(q);
      const wantedPedals = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setUserWantedPedals(prev => {
        const prevJson = JSON.stringify(prev.map(p => p.id).sort());
        const newJson = JSON.stringify(wantedPedals.map(p => p.id).sort());
        if (prevJson === newJson) return prev;
        return wantedPedals;
      });
    } catch (error) {
      console.error('Error loading wanted pedals:', error);
      setUserWantedPedals(prev => prev);
    }
  };

  const loadPotentialMatches = async () => {
    if (!user) {
      setPotentialMatchUserIds(new Set());
      return;
    }
    try {
      const wmpSnapshot = await getDocs(
        query(
          collection(db, 'wantsMyPedals'),
          where('pedalOwnerId', '==', user.uid)
        )
      );
      const mySaleOnly = await getMySaleOnlyPedalIds(user.uid); // plan §5
      const userIds = new Set();
      wmpSnapshot.docs.forEach(d => { if (!mySaleOnly.has(d.data().pedalId)) userIds.add(d.data().wantingUserId); });
      setPotentialMatchUserIds(userIds);
    } catch (error) {
      console.error('Error loading potential matches:', error);
    }
  };

  const loadFeaturedPedals = async (reset = false) => {
    // Block concurrent appends — they'd all use the same lastVisible cursor
    // and fetch identical pedals. Resets are allowed through because filter
    // changes must always take effect; superseded resets are handled by the
    // token check below (their results are discarded).
    if (!reset && fetchInFlightRef.current) {
      return;
    }
    // Back-navigation restoration (2026-10-31): the restored cards are in
    // state at once, but the cursor behind them is resolved asynchronously.
    // An append in that window would run batch 1 again from the top and
    // re-fetch listings already on screen; wait for the cursor instead.
    if (!canAppendBrowse({ reset, hasPedals: featuredPedals.length > 0, hasCursor: !!lastVisible })) {
      return;
    }

    // Issue a unique token. Any earlier in-flight fetches are now stale and
    // will discard their results when they resolve.
    const myToken = ++fetchTokenRef.current;
    fetchInFlightRef.current = true;
    setBrowseLoadFailed(false);

    if (reset) {
      setIsLoadingFeatured(true);
    } else {
      setIsLoadingMore(true);
    }

    // Whole-loader timeout. If the entire fetch (multiple Firestore reads +
    // owner enrichment + cursor resolve) hangs past FETCH_TIMEOUT_MS, give
    // up and surface a retryable error. The underlying getDocs calls can't
    // actually be cancelled — Firestore will keep retrying in the background
    // — but the token-based supersede logic below discards whatever they
    // eventually resolve to, so a stale zombie fetch won't clobber state.
    const doFetch = async () => {
      const TARGET_COUNT = 28; // 7 complete rows in 4-column grid
      const BATCH_SIZE = 50;
      let accumulatedPedals = [];
      let currentLastVisible = reset ? null : lastVisible;
      const scanStartedAt = Date.now();
      let batchesThisLoad = 0;
      let scanPausedThisLoad = false;
      // Every fetched doc snapshot by id: the cursor for the next page is the
      // snapshot of the last SHOWN pedal, which is always one of these, so
      // it no longer costs a getDoc round trip per page (2026-10-31).
      const fetchedDocById = new Map();
      let keepFetching = true;
      
      // Pre-process search query for filtering
      const searchLower = searchQuery.trim().toLowerCase();
      const normalize = (str) => str ? str.toLowerCase().replace(/['''\u2019\-]/g, '') : '';
      const normalizedQuery = normalize(searchQuery.trim());
      const queryWords = normalizedQuery.split(/\s+/).filter(Boolean);
      const allWordsMatch = (str) => {
        const normalizedStr = normalize(str);
        return queryWords.every(word => normalizedStr.includes(word));
      };
      const hasSearchQuery = searchLower.length > 0;

      // NEW: Reverse search mode - find users who want the searched item
      let usersWhoWantThis = new Set();
      if (searchUserWants && hasSearchQuery) {
        try {
          // Fetch all Wantlist items
          const wantListQuery = query(collection(db, 'wantList'));
          const wantListSnapshot = await getDocs(wantListQuery);
          
          // Filter wants that match the search query
          wantListSnapshot.docs.forEach(wantDoc => {
            const want = wantDoc.data();
            
            const combinedWantName = `${want.make || ''} ${want.model || ''}`.toLowerCase();
            const matchesSearch = 
              combinedWantName.includes(searchLower) ||
              want.make?.toLowerCase().includes(searchLower) ||
              want.model?.toLowerCase().includes(searchLower) ||
              (want.effectTypes && Array.isArray(want.effectTypes) && 
               want.effectTypes.some(type => type.toLowerCase().includes(searchLower)));
            
            if (matchesSearch && want.uid) {
              usersWhoWantThis.add(want.uid);
            }
          });
          
        } catch (error) {
          console.error('Error fetching Wantlists:', error);
        }
      }

      const FIRESTORE_ARRAY_CONTAINS_ANY_LIMIT = 10;
      let effectTypesForQuery = filterEffectTypes;
      if (filterEffectTypes.length > FIRESTORE_ARRAY_CONTAINS_ANY_LIMIT) {
        console.warn(
          `Browse: effect type filter capped at ${FIRESTORE_ARRAY_CONTAINS_ANY_LIMIT} types for Firestore array-contains-any`,
          { selected: filterEffectTypes.length }
        );
        effectTypesForQuery = filterEffectTypes.slice(0, FIRESTORE_ARRAY_CONTAINS_ANY_LIMIT);
      }

      // Keep fetching batches until we have 28 pedals that pass all active filters
      while (keepFetching && accumulatedPedals.length < TARGET_COUNT) {
        const constraints = [
          where('active', '==', true),
        ];
        if (effectTypesForQuery.length === 1) {
          constraints.push(where('effectTypes', 'array-contains', effectTypesForQuery[0]));
        } else if (effectTypesForQuery.length > 1) {
          constraints.push(where('effectTypes', 'array-contains-any', effectTypesForQuery));
        }
        constraints.push(orderBy('listedAt', 'desc'));
        if (currentLastVisible) {
          constraints.push(startAfter(currentLastVisible));
        }
        constraints.push(limit(BATCH_SIZE));
        const q = query(collection(db, 'listings'), ...constraints);

        const snapshot = await getDocs(q);

        // Localhost validation caught this (2026-09-29): an OFFLINE client's
        // getDocs does not reject — it resolves with an empty cache-served
        // snapshot, which used to render as a legitimately empty marketplace.
        // Throw it into the retry/failed path instead; a cache result WITH
        // data passes through (stale cards beat a false error).
        throwIfOfflineEmpty(snapshot, 'loadFeaturedPedals');

        if (snapshot.docs.length === 0 && accumulatedPedals.length === 0) {
          console.error('⚠️ WARNING: Query returned 0 documents on first batch. Possible causes:');
          console.error('1. No active listings in database');
          console.error('2. Firestore composite index missing');
          console.error('3. AppCheck blocking requests');
          console.error('Query details:', { hasLastVisible: !!currentLastVisible, filterType, hasSearchQuery, effectTypesForQuery });
        }

        // If we got fewer than BATCH_SIZE, we've reached the end
        if (snapshot.docs.length < BATCH_SIZE) {
          keepFetching = false;
        }

        // Track last document for next batch
        if (snapshot.docs.length > 0) {
          currentLastVisible = snapshot.docs[snapshot.docs.length - 1];
        } else {
          // No more documents
          keepFetching = false;
          break;
        }

        batchesThisLoad += 1;

        // Collect pedals and unique owner IDs from this batch
        const batchPedals = [];
        const ownerIds = new Set();
        
        for (const docSnap of snapshot.docs) {
          fetchedDocById.set(docSnap.id, docSnap);
          const pedalData = docSnap.data();
          
          // Skip pedals that are currently in trades
          if (pedalData.inTrade) continue;
          
          // Skip pedals hidden from pedalboards 
          if (pedalData.hiddenFromPedalboard) continue;
          
          // Apply reverse search filter (users who want the searched item)
          if (searchUserWants && hasSearchQuery) {
            // Only show pedals from users who want the searched item
            if (!usersWhoWantThis.has(pedalData.ownerId)) continue;
          } else if (hasSearchQuery) {
            const combinedName = `${pedalData.make || ''} ${pedalData.model || ''}`.toLowerCase();
            const matchesSearch = 
              combinedName.includes(searchLower) ||
              normalize(combinedName).includes(normalizedQuery) ||
              allWordsMatch(combinedName) ||
              pedalData.make?.toLowerCase().includes(searchLower) ||
              normalize(pedalData.make).includes(normalizedQuery) ||
              pedalData.model?.toLowerCase().includes(searchLower) ||
              normalize(pedalData.model).includes(normalizedQuery) ||
              (pedalData.effectTypes && Array.isArray(pedalData.effectTypes) && 
               pedalData.effectTypes.some(type => 
                 type.toLowerCase().includes(searchLower) ||
                 normalize(type).includes(normalizedQuery)
               ));
            
            if (!matchesSearch) continue;
          }
          
          // Apply category filter
          if (categoryFilter !== 'all') {
            const ACCESSORY_TYPES = ['Power Supply', 'Pedalboard', 'Other Accessory'];
            const hasEffectType = pedalData.effectTypes?.some(t => !ACCESSORY_TYPES.includes(t));
            const pedalCategory = hasEffectType ? 'pedal' : 'accessory';
            if (categoryFilter === 'pedals' && pedalCategory !== 'pedal') continue;
            if (categoryFilter === 'accessories' && pedalCategory !== 'accessory') continue;
          }

          if (filterType === 'wanted') {
            if (user?.uid && pedalData.ownerId === user.uid) continue;
            if (userWantedPedals.length === 0) {
              continue; // No Wantlist items — skip all pedals, show empty state
            }
            const matchesWanted = userWantedPedals.some(wanted => doesPedalMatchWant(wanted, {
              ...pedalData,
              id: docSnap.id
            }));
            if (!matchesWanted) continue;
          }

          // Price / condition / decade (effect type is filtered server-side above)
          if (filterPrice.min !== '') {
            const min = Number(filterPrice.min);
            if (pedalData.price < min) continue;
          }
          if (filterPrice.max !== '') {
            const max = Number(filterPrice.max);
            if (pedalData.price > max) continue;
          }
          if (filterConditions.length > 0 && !filterConditions.includes(pedalData.condition)) {
            continue;
          }
          if (filterDecades.length > 0 && !filterDecades.includes(pedalData.decade)) {
            continue;
          }
          if (filterOffers.length > 0 && !matchesOfferFilter(pedalData, filterOffers)) {
            continue;
          }
          
          // Sort key: listedAt → reactivatedAt → createdAt
          const effectiveDate = listingListedAtMs(pedalData);
          
          batchPedals.push({
            id: docSnap.id,
            ...pedalData,
            effectiveDate
          });
          
          ownerIds.add(pedalData.ownerId);
        }

        // Owner enrichment happens ONCE, after the slice below — profiles are
        // fetched only for the 28 pedals actually rendered, not for owners of
        // every fetched batch (up to 22 of which the slice discards).

      // Add to accumulated pedals (raw — enriched after the slice)
      accumulatedPedals = [...accumulatedPedals, ...batchPedals];
      if (keepFetching && shouldPauseScan({ found: accumulatedPedals.length, target: TARGET_COUNT, batches: batchesThisLoad, elapsedMs: Date.now() - scanStartedAt, maxBatches: MAX_BATCHES_PER_LOAD, budgetMs: SCAN_BUDGET_MS })) {
        keepFetching = false;
        scanPausedThisLoad = true;
      }
    }

    // Sort by listedAt priority (newest first)
    accumulatedPedals.sort((a, b) => b.effectiveDate - a.effectiveDate);

    // All filters are applied during accumulation; take exactly 28 for a complete grid
    const shownRaw = accumulatedPedals.slice(0, TARGET_COUNT);

    // Owner enrichment for the pedals actually shown. publicProfiles is
    // publicly readable (allow read: if true in Firestore rules), so this is
    // NOT gated on `user` — the previous `user &&` guard meant logged-out
    // visitors always saw "User" with no photo/tier/trade-count. Batched via
    // documentId()-in chunks: one round trip instead of one per owner, which
    // is what matters under the forced long-polling in the native shell.
    const shownOwnerIds = Array.from(new Set(shownRaw.map(p => p.ownerId).filter(Boolean)));
    const profileById = await fetchPublicProfiles(shownOwnerIds, { cache: userDataCache });
    if (shownOwnerIds.length > 0) {
      setUserDataCache(prev => ({ ...prev, ...profileById }));
    }

    // Adding `membershipTier` + `ownerPhotoURL` lets the card render the owner
    // lockup (photo + tier badge + trade-count badge) in its info pane,
    // matching the Recommendations / Wantlist Matches treatment.
    const pedalsToShow = shownRaw.map(pedal => {
      const u = profileById[pedal.ownerId] || {};
      return {
        ...pedal,
        owner: {
          username: u.username || 'User',
          badge: getTradeBadge(u.completedTrades || 0),
          membershipTier: u.membershipTier,
          // lastActiveAt is no longer mirrored — the badge utility prefers lastActiveWeek.
          lastActiveWeek: u.lastActiveWeek || null,
          responderBadge: u.responderBadge || null
        },
        ownerPhotoURL: u.photoURL
      };
    });
    
    // Resolve the cursor doc for the last SHOWN pedal (not last FETCHED) so
    // startAfter() continues in listedAt desc order from pedal #29 onward.
    let resolvedLastVisible = currentLastVisible;
    if (pedalsToShow.length > 0) {
      const lastShownPedal = pedalsToShow[pedalsToShow.length - 1];
      try {
        // Reuse the snapshot we already fetched; fall back to a read only if
        // the shown pedal somehow did not come from this loader's batches.
        const lastShownDoc = fetchedDocById.get(lastShownPedal.id) || await getDoc(doc(db, 'listings', lastShownPedal.id));
        if (lastShownDoc.exists()) {
          resolvedLastVisible = lastShownDoc;
        } else {
          console.warn(
            'Browse pagination: last shown listing doc missing; falling back to batch cursor',
            { listingId: lastShownPedal.id }
          );
        }
      } catch (err) {
        console.error('Error resolving lastVisible cursor:', err);
        console.warn(
          'Browse pagination: could not fetch last shown listing; falling back to batch cursor',
          { listingId: lastShownPedal.id }
        );
      }
    }

    // STALE-FETCH GUARD: a newer fetch was issued while we were awaiting.
    // Discard our results entirely so we don't clobber the newer fetch's state.
    if (fetchTokenRef.current !== myToken) {
      return;
    }

    setLastVisible(resolvedLastVisible);

    if (reset) {
      setFeaturedPedals(pedalsToShow);
    } else {
      // DEDUPE GUARD: even with the token-based concurrency guard, a stale
      // observer closure (or any other race) could try to append pedals that
      // already exist in state. Drop any IDs we already have so React never
      // sees duplicate keys and the user never sees duplicate cards.
      setFeaturedPedals(prev => {
        const existingIds = new Set(prev.map(p => p.id));
        const newPedals = pedalsToShow.filter(p => !existingIds.has(p.id));
        return [...prev, ...newPedals];
      });
    }

      const hasMoreResults = keepFetching || accumulatedPedals.length > TARGET_COUNT || scanPausedThisLoad;
      setHasMore(hasMoreResults);
      setScanPaused(scanPausedThisLoad);

    };

    // Timeout + one delayed retry, with the outcome reported honestly
    // (Phase 2a, 2026-09-29 — the control flow is unchanged, extracted to
    // utils/recoverableLoad.js where it is unit-tested). Until this change
    // a double failure fell through to the LEGITIMATE empty-state UI, so an
    // offline blip made Browse claim the marketplace was empty with no way
    // back short of a full refresh.
    const outcome = await runWithTimeoutAndRetry(doFetch, {
      timeoutMs: FETCH_TIMEOUT_MS,
      retryDelayMs: RETRY_DELAY_MS,
      isCurrent: () => fetchTokenRef.current === myToken,
      label: 'loadFeaturedPedals'
    });
    if (outcome === 'failed' && reset) {
      // Still the current fetch (runWithTimeoutAndRetry only reports
      // 'failed' while current): render the couldn't-load state and let the
      // recovery effect arm the one-shot reconnect reload.
      // RESET LOADS ONLY (review S1): a failed APPEND (infinite scroll
      // during a blip) shows no band — cards are on screen — and arming
      // recovery for it would run a hidden full reset on reconnect,
      // silently discarding the user's pagination and scroll position.
      // Appends keep their pre-existing silent behavior.
      setBrowseLoadFailed(true);
    }
    // Only clear loading state if we are still the latest fetch. If we've
    // been superseded, leave it to the newer fetch to clear (otherwise we'd
    // race with it and prematurely re-enable the observer with stale state).
    if (fetchTokenRef.current === myToken) {
      fetchInFlightRef.current = false;
      setIsLoadingFeatured(false);
      setIsLoadingMore(false);
    }
  };

  // Shared retry for the couldn't-load state: the manual Try-again button
  // and the auto-recovery arm both come through here, so both respect the
  // in-flight guard and both reload the TAB that actually failed (the
  // Suggested tab has its own loader; re-running the All loader over a
  // failed Suggested view would silently switch the user's content).
  const retryFailedBrowseLoad = () => {
    if (fetchInFlightRef.current) return undefined;
    // Returned so a manual retry can wait for the reload to finish.
    if (filterType === 'suggested') {
      return loadSuggestedListings();
    }
    return loadFeaturedPedals(true);
  };

  // Auto-recovery for a FAILED load only (Phase 2a): while the couldn't-load
  // state shows, the first reconnect signal (browser 'online', or the tab
  // becoming visible) triggers ONE fresh reset load. armRecoveryOnReconnect
  // is one-shot by construction and retryFailedBrowseLoad checks the
  // in-flight ref, so overlapping online/visibility/manual signals cannot
  // stack concurrent loads. A successful-but-empty load never sets
  // browseLoadFailed, so it can never arm this. The cleanup disarms on
  // unmount/navigation (and on tab switch, which re-arms with the fresh
  // filterType), and the fetch token discards any stale resolve.
  useEffect(() => {
    if (!browseLoadFailed) return undefined;
    return armRecoveryOnReconnect(retryFailedBrowseLoad);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [browseLoadFailed, filterType]);

  /**
   * Loader for the "Suggested" tab. Reads the precomputed
   * /suggestedListings/{uid} doc, batch-fetches the live listings, drops any
   * that have gone stale (sold/deleted/inTrade/hidden/own-listing), and
   * attaches each suggestion's reason metadata as `_reason` so PedalCard can
   * render the "why" tag.
   *
   * Suggestions are a fixed-size top-N list — no pagination, no infinite
   * scroll. We set hasMore=false so the sentinel/loadMore UI stays hidden.
   */
  const loadSuggestedListings = async () => {
    if (!user) {
      setFeaturedPedals([]);
      setIsLoadingFeatured(false);
      setHasMore(false);
      // V4 (mirror of the dashboard's R3): a stale couldn't-load band must
      // not survive sign-out — with the flag stuck, Try-again would hit this
      // early return forever and the band's button would be a silent no-op.
      setBrowseLoadFailed(false);
      return;
    }

    const myToken = ++fetchTokenRef.current;
    fetchInFlightRef.current = true;
    setBrowseLoadFailed(false);
    setIsLoadingFeatured(true);
    setHasMore(false);

    // Whole-loader timeout — see loadFeaturedPedals for rationale.
    const doFetch = async () => {
      const sugDoc = await getDoc(doc(db, 'suggestedListings', user.uid));
      if (!sugDoc.exists()) {
        if (fetchTokenRef.current === myToken) {
          setFeaturedPedals([]);
        }
        return;
      }

      const data = sugDoc.data();
      const suggestions = Array.isArray(data.suggestions) ? data.suggestions : [];

      // Preserve order; build lookup
      const reasonByListingId = new Map();
      const orderedListingIds = [];
      for (const s of suggestions) {
        if (!s || !s.listingId || reasonByListingId.has(s.listingId)) continue;
        reasonByListingId.set(s.listingId, s.reason || null);
        orderedListingIds.push(s.listingId);
      }

      if (orderedListingIds.length === 0) {
        if (fetchTokenRef.current === myToken) {
          setFeaturedPedals([]);
        }
        return;
      }

      // Batch-fetch listings — Firestore `in` query allows up to 30 IDs/batch
      const listingMap = new Map();
      const BATCH = 30;
      for (let i = 0; i < orderedListingIds.length; i += BATCH) {
        const slice = orderedListingIds.slice(i, i + BATCH);
        try {
          const snap = await getDocs(
            query(collection(db, 'listings'), where(documentId(), 'in', slice))
          );
          // Offline, this query RESOLVES cache-empty instead of rejecting
          // (see throwIfOfflineEmpty) — without the rethrow below, every
          // suggestion would silently classify as "deleted" and the tab
          // would render a false "No suggestions yet."
          throwIfOfflineEmpty(snap, 'loadSuggestedListings');
          snap.forEach(d => listingMap.set(d.id, { id: d.id, ...d.data() }));
        } catch (err) {
          // R4: transient REJECTIONS (unavailable / network blip) must reach
          // the retry/failed path just like the cache-empty resolve —
          // swallowing them classified every suggestion as "deleted" and
          // rendered the false "No suggestions yet". Non-transient per-slice
          // errors keep degrading per-slice, as before.
          if (isTransientConnectivityError(err)) throw err;
          console.error('Batched listing fetch failed for slice:', err);
        }
      }

      // Filter out stale entries silently
      const ACCESSORY_TYPES = ['Power Supply', 'Pedalboard', 'Other Accessory'];
      const hydrated = [];
      const ownerIds = new Set();
      for (const id of orderedListingIds) {
        const listing = listingMap.get(id);
        if (!listing) continue;                       // deleted
        if (listing.active === false) continue;       // deactivated
        if (listing.inTrade) continue;                // mid-trade
        if (listing.hiddenFromPedalboard) continue;   // owner hid it
        if (listing.ownerId === user.uid) continue;   // shouldn't happen, but be safe

        // Respect category filter (consistent with All Listings tab)
        if (categoryFilter !== 'all') {
          const hasEffectType = listing.effectTypes?.some(t => !ACCESSORY_TYPES.includes(t));
          const pedalCategory = hasEffectType ? 'pedal' : 'accessory';
          if (categoryFilter === 'pedals' && pedalCategory !== 'pedal') continue;
          if (categoryFilter === 'accessories' && pedalCategory !== 'accessory') continue;
        }

        hydrated.push({
          ...listing,
          _reason: reasonByListingId.get(id),
          effectiveDate: listing.reactivatedAt
            ? new Date(listing.reactivatedAt)
            : (listing.createdAt?.toDate?.() || new Date(listing.createdAt || 0))
        });
        ownerIds.add(listing.ownerId);
      }

      // Hydrate owner display info from publicProfiles (same batched pattern
      // as loadFeaturedPedals — one documentId()-in round trip per 30 owners).
      const newUserData = ownerIds.size > 0
        ? await fetchPublicProfiles(Array.from(ownerIds), { cache: userDataCache })
        : {};
      if (ownerIds.size > 0) {
        setUserDataCache(prev => ({ ...prev, ...newUserData }));
      }

      const enriched = hydrated.map(p => {
        const u = newUserData[p.ownerId] || userDataCache[p.ownerId] || {};
        return {
          ...p,
          owner: {
            username: u.username || 'User',
            badge: getTradeBadge(u.completedTrades || 0),
            membershipTier: u.membershipTier,
            lastActiveWeek: u.lastActiveWeek || null,
            responderBadge: u.responderBadge || null
          },
          ownerPhotoURL: u.photoURL
        };
      });

      // STALE-FETCH GUARD: discard if a newer fetch superseded us
      if (fetchTokenRef.current !== myToken) {
        return;
      }

      setFeaturedPedals(applyAdvancedFilters(enriched));
      setSuggestedSource(data.source || null);
    };

    // Same outcome-reporting flow as loadFeaturedPedals: a double failure
    // must render the couldn't-load band, never a false "No suggestions yet."
    const outcome = await runWithTimeoutAndRetry(doFetch, {
      timeoutMs: FETCH_TIMEOUT_MS,
      retryDelayMs: RETRY_DELAY_MS,
      isCurrent: () => fetchTokenRef.current === myToken,
      label: 'loadSuggestedListings'
    });
    if (outcome === 'failed') {
      setBrowseLoadFailed(true);
    }
    if (fetchTokenRef.current === myToken) {
      fetchInFlightRef.current = false;
      setIsLoadingFeatured(false);
      setIsLoadingMore(false);
    }
  };

  const pullToRefreshRef = useRef(null);
  pullToRefreshRef.current = async () => {
    isRestoringState.current = false;
    hasRestoredState.current = false;
    setFeaturedPedals([]);
    setLastVisible(null);
    setHasMore(true);
    setAutoLoadCount(0);
    if (filterType === 'suggested') {
      await loadSuggestedListings();
    } else {
      setSuggestedSource(null);
      await loadFeaturedPedals(true);
    }
    if (user) {
      await Promise.all([loadUserWantedPedals(), loadPotentialMatches()]);
    }
  };

  const refreshBrowse = useCallback(async () => {
    await pullToRefreshRef.current?.();
  }, []);

  useRegisterPullToRefresh(refreshBrowse);

  const loadMore = () => {
    // Suggested tab is a finite top-N list — no pagination
    if (filterType === 'suggested') return;
    if (!isLoadingMore && hasMore) {
      // Manual click resets the auto-load cap so infinite scroll resumes
      setAutoLoadCount(0);
      loadFeaturedPedals(false);
    }
  };

  // Reset auto-load cap whenever the filter/search context changes
  // (the pedal list is wiped and rebuilt from scratch in those cases).
  useEffect(() => {
    setAutoLoadCount(0);
  }, [filterType, categoryFilter, searchQuery, searchUserWants]);

  // Infinite scroll: observe a sentinel below the grid and auto-trigger
  // the next page until we hit the soft cap. Manual button is the fallback.
  useEffect(() => {
    if (!SUPPORTS_INTERSECTION_OBSERVER) return;
    if (!sentinelRef.current) return;
    if (!hasMore) return;
    if (isLoadingMore || isLoadingFeatured) return;
    if (featuredPedals.length === 0) return;
    if (autoLoadCount >= (activeFilterCount > 0 ? Infinity : MAX_AUTO_LOADS)) return;
    // Restoration: cards are on screen before their cursor is resolved; an
    // auto-load now would re-fetch from the top. The effect re-runs when
    // lastVisible arrives (it is in the deps).
    if (!lastVisible) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;
        // Skip during scroll-position restoration so we don't auto-fire
        // before the user has had a chance to land back where they left off.
        if (isRestoringState.current) return;
        setAutoLoadCount((c) => c + 1);
        loadFeaturedPedals(false);
      },
      { rootMargin: SENTINEL_ROOT_MARGIN }
    );

    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
    // IMPORTANT: lastVisible MUST be in deps. Without it, the observer closes
    // over a stale `loadFeaturedPedals` whose `lastVisible` is whatever it was
    // when the observer attached. In the saved-state restoration path,
    // featuredPedals is populated synchronously but lastVisible is set
    // asynchronously by a later getDoc(). If the observer attaches before that
    // resolves, every fetch it triggers reuses lastVisible=null and re-fetches
    // batch 1 — which is what caused the duplicate-key warnings.
  }, [hasMore, isLoadingMore, isLoadingFeatured, autoLoadCount, featuredPedals.length, lastVisible]);

  const getTradeBadge = (count) => {
    if (count === undefined || count === null || isNaN(count)) return 0;
    return count; // Show exact number of completed trades, including 0
  };

  const renderMobileChromeSearchRow = (searchInputId) => {
    const reverseSearchId = `search-user-wants-${searchInputId}`;
    return (
    <div style={{ maxWidth: '1100px', margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>
      <div className="latest-header-compact-row1" style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
        <div style={{ flex: 1, position: 'relative' }}>
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#9CA3AF"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
            aria-hidden="true"
          >
            <circle cx="11" cy="11" r="7" />
            <line x1="20" y1="20" x2="16.65" y2="16.65" />
          </svg>
          <input
            id={searchInputId}
            className="latest-header-compact-search-input"
            type="text"
            placeholder="Search make, model, user..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            aria-label="Search pedals"
            style={{
              width: '100%',
              borderRadius: '9999px',
              border: '1px solid #d1d5db',
              backgroundColor: 'white',
              fontFamily: 'Nunito, sans-serif',
              fontSize: '16px',
              padding: '0.55rem 1rem 0.55rem 2.5rem',
              outline: 'none',
              boxSizing: 'border-box',
              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)'
            }}
          />
          <UserSearchSuggestions searchText={searchQuery} />
        </div>
        <button
          className="latest-header-compact-filter-btn latest-header-compact-filter-btn-mobile"
          onClick={openFilterSheet}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '6px',
            padding: '0.6rem 1.4rem',
            borderRadius: '9999px',
            border: '1px solid #C89F3C',
            backgroundColor: activeFilterCount > 0 ? '#C89F3C' : 'white',
            color: activeFilterCount > 0 ? 'white' : '#374151',
            fontFamily: 'Nunito, sans-serif',
            fontWeight: 700,
            fontSize: '1rem',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            flexShrink: 0
          }}
          aria-label="Open filters"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <line x1="4" y1="6" x2="20" y2="6"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="10" y1="18" x2="14" y2="18"/>
          </svg>
          Filter{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
        </button>
      </div>
      <label
        className="latest-header-compact-reverse-search"
        style={{
          display: 'grid',
          gridTemplateColumns: '16px 1fr',
          columnGap: '6px',
          alignItems: 'start',
          marginTop: '10px',
          cursor: 'pointer',
          width: '100%',
        }}
      >
        <input
          type="checkbox"
          id={reverseSearchId}
          checked={searchUserWants}
          onChange={(e) => setSearchUserWants(e.target.checked)}
          style={{
            width: '16px',
            height: '16px',
            margin: '2px 0 0 0',
            padding: 0,
            cursor: 'pointer',
            accentColor: '#5A9ECB',
          }}
        />
        <span
          className="latest-header-compact-reverse-search-text"
          style={{
            fontSize: 'calc(0.8rem + 1pt)',
            color: '#5A9ECB',
            userSelect: 'none',
            lineHeight: '1.35',
            textAlign: 'left',
          }}
        >
          <strong>Reverse search:</strong> see users who WANT what you search
        </span>
      </label>
    </div>
  );
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F7F3EB', minHeight: 'calc(100vh - var(--navbar-height, 125px))', display: 'flex', flexDirection: 'column' }}>
      {/* BottomTabBar adds body { padding-bottom: 150px } on mobile to clear the
          fixed tab bar. When the tab content is short (e.g. the Accessories
          filter with few results), the body's default white background bleeds
          through that padding band as a white stripe at the bottom. Pin a
          150px beige stripe to the BOTTOM of the viewport via a fixed-attached
          gradient so only that band is tinted — the area near the navbar
          stays pure white. Scoped to this component's lifetime; the body bg
          reverts when the user navigates away. */}
      <style jsx>{`
        body {
          background:
            linear-gradient(to bottom,
              #ffffff 0%,
              #ffffff calc(100% - 150px),
              #F7F3EB calc(100% - 150px),
              #F7F3EB 100%) fixed !important;
        }
      `}</style>
      <SEO
        title="Browse Pedals — PedalXchange"
        description="Search, filter, and browse every active guitar pedal listing on PedalxChange. Find your next trade or purchase."
        url="https://pedalxchange.com/latest-pedals"
        keywords="browse guitar pedals, search pedal listings, trade guitar pedals, buy effects pedals, pedal marketplace"
      />
      
      {/* Header section — wraps the expanded hero (in normal page flow) and
          the compact bar (overlaid as position: fixed when active). Keeping
          the expanded hero in normal flow avoids the layout jump that
          happened when we used `display: none` to swap between the two:
          removing ~400px of content under your finger felt like a scroll
          snap-back to the top on mobile. */}
      <div
        className={`latest-sticky-header ${isHeaderCompact ? 'is-compact' : ''}`}
        style={{
          width: '100%',
          boxSizing: 'border-box',
          padding: '2rem 1.5rem 1rem 1.5rem'
        }}
      >
        {/* Unscoped styles — styled-jsx hashing was preventing mobile overrides
            on .latest-header-compact / .is-mobile-visible from matching the DOM,
            leaving desktop rules (opacity:0, pointer-events:none) in effect. */}
        <style>{`
          .latest-sticky-header.is-compact {
            padding-top: 0.5rem !important;
            padding-bottom: 0.5rem !important;
            padding-left: 1rem !important;
            padding-right: 1rem !important;
            background-color: #F7F3EB;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
          }
          .latest-sticky-header .latest-header-expanded {
            display: block;
          }
          .latest-sticky-header .latest-mobile-chrome-slot {
            display: none;
          }
          .latest-sticky-header .latest-header-compact-filter-btn-mobile {
            display: none;
          }
          .latest-sticky-header .latest-header-compact-mobile-fixed {
            display: none;
          }
          @media (min-width: 769px) {
            .latest-sticky-header:not(.is-compact) {
              padding-left: 0 !important;
              padding-right: 0 !important;
              padding-bottom: 0.5rem !important;
            }
            .latest-sticky-header .latest-header-expanded-inner {
              max-width: 1600px;
              width: 100%;
              margin-left: auto;
              margin-right: auto;
              margin-bottom: 1.25rem !important;
              padding-left: 2rem;
              padding-right: 2rem;
              box-sizing: border-box;
            }
            .latest-pedals-grid-section {
              padding-top: 1.5rem !important;
            }
            .latest-sticky-header .latest-header-grid {
              display: flex;
              flex-wrap: wrap;
              align-items: flex-start;
              gap: 0.5rem 5rem;
            }
            .latest-sticky-header .latest-header-copy {
              flex: 0 1 auto;
              min-width: 0;
            }
            .latest-sticky-header .latest-header-copy h1 {
              margin: 0;
              padding: 0;
              text-align: left !important;
            }
            .latest-sticky-header .latest-header-copy .latest-header-subtitle {
              margin: 0;
              padding: 0;
              text-align: left !important;
              max-width: none !important;
            }
            .latest-sticky-header .latest-header-search-col {
              flex: 1 1 auto;
              min-width: 500px;
              width: auto;
              max-width: none;
              margin-right: 1rem;
              display: flex;
              flex-direction: column;
              gap: 0.5rem;
            }
            .latest-sticky-header .latest-header-search-col .latest-search-bar {
              width: 100%;
              max-width: none;
              margin: 0;
              padding: 0;
            }
            .latest-sticky-header .latest-header-search-col .latest-header-search-extra {
              margin: 0;
            }
            .latest-sticky-header .latest-header-search-extra .reverse-search-checkbox {
              max-width: none !important;
              margin: 0 !important;
              padding: 0 !important;
            }
            .latest-sticky-header .latest-header-search-extra .reverse-search-label {
              color: #5A9ECB !important;
            }
            @media (max-width: 900px) {
              .latest-sticky-header .latest-header-grid {
                flex-direction: column;
              }
              .latest-sticky-header .latest-header-search-col {
                flex: 0 1 auto;
                min-width: 0;
                width: 100%;
                max-width: 100%;
              }
              .latest-sticky-header .latest-header-search-col .latest-search-bar {
                width: 100%;
                max-width: 100%;
              }
            }
          }
          /* Desktop compact bar — unchanged threshold/opacity behavior */
          @media (min-width: 769px) {
            .latest-sticky-header .latest-header-compact-mobile-fixed {
              display: none !important;
            }
            .latest-sticky-header .latest-header-compact {
              position: fixed;
              top: 0;
              left: 0;
              right: 0;
              z-index: 100;
              padding: 0.6rem 1rem 0.7rem 1rem;
              background-color: #F7F3EB;
              box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
              box-sizing: border-box;
              opacity: 0;
              pointer-events: none;
              transition: opacity 0.2s ease;
            }
            .latest-sticky-header.is-compact .latest-header-compact {
              opacity: 1;
              pointer-events: auto;
            }
            .latest-sticky-header .latest-header-compact-search-btn {
              padding: 0.5rem 0.9rem !important;
              font-size: 0.9rem !important;
            }
            .latest-sticky-header .latest-header-compact-toggle-btn {
              padding: 0.55rem 1.4rem !important;
            }
            .latest-sticky-header .latest-header-compact-filter-btn {
              padding: 0.6rem 1.4rem !important;
            }
          }
          /* Mobile: in-flow bar scrolls away naturally; fixed overlay on scroll-up. */
          @media (max-width: 768px) {
            .latest-sticky-header {
              padding: 0 !important;
            }
            .latest-sticky-header .latest-header-expanded {
              display: none !important;
            }
            .latest-sticky-header .latest-mobile-chrome-slot {
              display: block !important;
              position: relative;
              flex-shrink: 0;
              background-color: #F7F3EB;
              overflow: hidden;
            }
            .latest-sticky-header .latest-header-compact-inflow {
              display: block;
              padding: 0.6rem 1rem 0.7rem 1rem;
              background-color: #F7F3EB;
              box-sizing: border-box;
            }
            .latest-sticky-header .latest-header-compact-inflow.is-suppressed {
              visibility: hidden;
              pointer-events: none;
            }
            .latest-sticky-header .latest-header-compact-desktop {
              display: none !important;
            }
            .latest-sticky-header .latest-header-compact-mobile-fixed {
              display: block;
            }
            .latest-sticky-header .latest-header-compact {
              display: none;
              position: fixed;
              top: 0;
              left: 0;
              right: 0;
              z-index: 100;
              padding: 0.6rem 1rem 0.7rem 1rem;
              background-color: #F7F3EB;
              box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
              box-sizing: border-box;
              pointer-events: none;
              transform: translateY(-100%);
              transition: transform 0.2s ease;
            }
            .latest-sticky-header .latest-header-compact.is-mobile-visible {
              display: block;
              transform: translateY(0);
              pointer-events: auto;
            }
            .latest-sticky-header .latest-header-compact-row1 {
              margin-bottom: 0 !important;
            }
            .latest-sticky-header .latest-header-compact-row2 {
              display: none !important;
            }
            .latest-sticky-header .latest-header-compact-filter-btn-mobile {
              display: inline-flex !important;
              padding: 0.5rem 1rem !important;
              font-size: 0.875rem !important;
            }
            .latest-sticky-header .latest-header-compact-search-btn {
              display: none !important;
            }
            .latest-sticky-header .latest-header-compact-search-input {
              font-size: 16px !important;
            }
            .latest-sticky-header .latest-header-compact-reverse-search {
              display: grid !important;
              grid-template-columns: 16px 1fr !important;
              column-gap: 6px !important;
              align-items: start !important;
              margin-top: 12px !important;
            }
            .latest-sticky-header .latest-header-compact-reverse-search input[type="checkbox"] {
              width: 16px !important;
              height: 16px !important;
              margin: 2px 0 0 0 !important;
              padding: 0 !important;
            }
            .latest-sticky-header .latest-header-compact-reverse-search-text {
              font-size: calc(0.75rem + 1pt) !important;
              color: #5A9ECB !important;
              text-align: left !important;
            }
          }
        `}</style>
        {/* Expanded hero — the full original header (title, copy, search,
            reverse-search checkbox, mobile category toggle). Collapses to height 0
            when isHeaderCompact is true; the compact bar below takes its place. */}
        <div className="latest-header-expanded" ref={headerExpandedRef}>
        <div className="latest-header-expanded-inner" style={{ marginBottom: '2rem' }}>
          <div className="latest-header-grid">
            <div className="latest-header-copy">
              <h1 className="text-3xl font-bold text-gray-900" style={{ fontSize: '2rem', lineHeight: '1.2' }}>
                Browse All Pedals
              </h1>
              <p className="latest-header-subtitle text-lg font-semibold text-gray-700" style={{ lineHeight: '1.6', fontSize: '20px' }}>
                Explore pedals added to the platform
              </p>
            </div>
            <div className="latest-header-search-col">
            <div className="latest-search-bar" style={{ position: 'relative' }}>
              <input
                type="text"
                placeholder="Search by make, model, effect type, or user..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="px-4 py-2 border border-gray-300 focus:border-blue-500 focus:outline-none shadow-sm"
                style={{
                  width: '100%',
                  borderRadius: '25px',
                  fontSize: '1rem',
                  paddingTop: '0.75rem',
                  paddingBottom: '0.75rem',
                  paddingLeft: '1.25rem'
                }}
              />
              <UserSearchSuggestions searchText={searchQuery} />
            </div>
            <div className="latest-header-search-extra">
              {/* Reverse Search Checkbox */}
              <div className="reverse-search-checkbox" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', gap: '12px' }}>
                <style jsx>{`
                  @media (min-width: 769px) {
                    #search-user-wants {
                      width: 20px !important;
                      height: 20px !important;
                      min-width: 20px !important;
                      min-height: 20px !important;
                    }
                  }
                  @media (max-width: 768px) {
                    .reverse-search-checkbox {
                      max-width: 500px !important;
                      padding: 0 1.5rem !important;
                      align-items: flex-start !important;
                      text-align: left !important;
                    }
                    .reverse-search-label {
                      font-size: 15px !important;
                    }
                  }
                `}</style>
                <input
                  type="checkbox"
                  id="search-user-wants"
                  checked={searchUserWants}
                  onChange={(e) => setSearchUserWants(e.target.checked)}
                  style={{
                    width: '18px',
                    height: '18px',
                    minWidth: '18px',
                    minHeight: '18px',
                    cursor: 'pointer',
                    accentColor: '#5A9ECB',
                    flexShrink: 0,
                    marginTop: '3px',
                    border: '2px solid #5A9ECB'
                  }}
                />
                <label
                  htmlFor="search-user-wants"
                  className="reverse-search-label"
                  style={{
                    fontSize: '16px',
                    color: '#374151',
                    cursor: 'pointer',
                    userSelect: 'none',
                    lineHeight: '1.4'
                  }}
                >
                  <strong>Reverse search:</strong> instead find users who WANT what you search for
                </label>
              </div>
            </div>
            </div>
          </div>

          {/* Mobile centered toggle: All / My Wants / Suggested (drives filterType).
              Logged-out users don't have wants/suggested, so we only render the
              toggle when a user is signed in (matches the desktop behavior). */}
          {user && (
            <div className="mobile-category-toggle" style={{ display: 'none', justifyContent: 'center', marginTop: '2.25rem', marginBottom: '-1rem' }}>
              <div style={{
                display: 'inline-flex',
                backgroundColor: 'white',
                borderRadius: '25px',
                padding: '4px',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                border: '1px solid #e5e7eb'
              }}>
                {['all', 'wanted', 'suggested'].map((val) => (
                  <button
                    key={val}
                    onClick={() => {
                      setFilterType(val);
                      if (val === 'suggested' && user) {
                        addDoc(collection(db, 'pageViewEvents'), {
                          pageKey: 'suggested-tab',
                          path: '/latest-pedals',
                          uid: user.uid,
                          createdAt: serverTimestamp(),
                        }).catch(() => {});
                      }
                    }}
                    style={{
                      padding: '0.5rem 1.5rem',
                      borderRadius: '25px',
                      border: 'none',
                      fontWeight: '600',
                      fontSize: '1rem',
                      fontFamily: 'Nunito',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      backgroundColor: filterType === val ? '#C89F3C' : 'transparent',
                      color: filterType === val ? 'white' : '#6B7280'
                    }}
                  >
                    {val === 'all' ? 'All' : val === 'wanted' ? 'My Wants' : 'Suggested'}
                  </button>
                ))}
              </div>
            </div>
          )}
          
        </div>
        </div>{/* /.latest-header-expanded */}

        {/* Mobile chrome slot — permanent height so listings never shift. */}
        <div
          ref={mobileChromeSlotRef}
          className="latest-mobile-chrome-slot"
          style={mobileChromeBarHeight > 0 ? { height: mobileChromeBarHeight, minHeight: mobileChromeBarHeight } : undefined}
        >
          <div
            ref={mobileChromeInflowRef}
            className={['latest-header-compact-inflow', showMobileChromeBar && 'is-suppressed'].filter(Boolean).join(' ')}
          >
            {renderMobileChromeSearchRow('latest-compact-search-input-inflow')}
          </div>
        </div>

        {/* Desktop: overlay when isHeaderCompact. Mobile: fixed reveal on scroll-up. */}
        <div
          className={[
            'latest-header-compact',
            showMobileChromeBar && 'is-mobile-visible',
          ].filter(Boolean).join(' ')}
        >
          <div className="latest-header-compact-mobile-fixed">
            {renderMobileChromeSearchRow('latest-compact-search-input-fixed')}
          </div>
          <div className="latest-header-compact-desktop" style={{ maxWidth: '1100px', margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>
          <div className="latest-header-compact-row1" style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
            <div style={{ flex: 1, position: 'relative' }}>
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#9CA3AF"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
                aria-hidden="true"
              >
                <circle cx="11" cy="11" r="7" />
                <line x1="20" y1="20" x2="16.65" y2="16.65" />
              </svg>
              <input
                id="latest-compact-search-input"
                className="latest-header-compact-search-input"
                type="text"
                placeholder="make, model, user..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                aria-label="Search pedals"
                style={{
                  width: '100%',
                  borderRadius: '9999px',
                  border: '1px solid #d1d5db',
                  backgroundColor: 'white',
                  fontFamily: 'Nunito, sans-serif',
                  fontSize: '16px',
                  padding: '0.55rem 1rem 0.55rem 2.5rem',
                  outline: 'none',
                  boxSizing: 'border-box',
                  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)'
                }}
              />
              <UserSearchSuggestions searchText={searchQuery} />
            </div>
            <button
              className="latest-header-compact-search-btn"
              onClick={() => {
                const el = document.getElementById('latest-compact-search-input');
                if (el) el.focus();
              }}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '6px',
                padding: '0.55rem 1.25rem',
                borderRadius: '9999px',
                border: 'none',
                backgroundColor: '#5A9ECB',
                color: 'white',
                fontFamily: 'Nunito, sans-serif',
                fontWeight: 700,
                fontSize: '16px',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                flexShrink: 0
              }}
              aria-label="Search"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="11" cy="11" r="7" />
                <line x1="20" y1="20" x2="16.65" y2="16.65" />
              </svg>
              Search
            </button>
          </div>
          {/* Desktop row 2: filterType toggle + Filter button */}
          <div className="latest-header-compact-row2" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
            {user ? (
              <div style={{
                display: 'inline-flex',
                backgroundColor: 'white',
                borderRadius: '9999px',
                padding: '3px',
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
                border: '1px solid #e5e7eb'
              }}>
                {['all', 'wanted', 'suggested'].map((val) => (
                  <button
                    key={val}
                    className="latest-header-compact-toggle-btn"
                    onClick={() => {
                      setFilterType(val);
                      if (val === 'suggested' && user) {
                        addDoc(collection(db, 'pageViewEvents'), {
                          pageKey: 'suggested-tab',
                          path: '/latest-pedals',
                          uid: user.uid,
                          createdAt: serverTimestamp(),
                        }).catch(() => {});
                      }
                    }}
                    style={{
                      padding: '0.55rem 1.4rem',
                      borderRadius: '9999px',
                      border: 'none',
                      fontWeight: 700,
                      fontSize: '1rem',
                      fontFamily: 'Nunito, sans-serif',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      transition: 'background-color 0.2s, color 0.2s',
                      backgroundColor: filterType === val ? '#C89F3C' : 'transparent',
                      color: filterType === val ? 'white' : '#6B7280'
                    }}
                  >
                    {val === 'all' ? 'All' : val === 'wanted' ? 'My Wants' : 'Suggested'}
                  </button>
                ))}
              </div>
            ) : <span /> /* Spacer so Filter still pushes to the right */}
            <button
              className="latest-header-compact-filter-btn"
              onClick={openFilterSheet}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '6px',
                padding: '0.6rem 1.4rem',
                borderRadius: '9999px',
                border: '1px solid #C89F3C',
                backgroundColor: activeFilterCount > 0 ? '#C89F3C' : 'white',
                color: activeFilterCount > 0 ? 'white' : '#374151',
                fontFamily: 'Nunito, sans-serif',
                fontWeight: 700,
                fontSize: '1rem',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                flexShrink: 0
              }}
              aria-label="Open filters"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <line x1="4" y1="6" x2="20" y2="6"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="10" y1="18" x2="14" y2="18"/>
              </svg>
              Filter{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
            </button>
          </div>
          </div>{/* /.latest-header-compact-desktop */}
        </div>
      </div>

      {/* Browse Pedals Grid Section */}
      <div className="latest-pedals-grid-section" style={{ flex: 1, paddingTop: '2rem', paddingBottom: '2rem' }}>
        
        {/* Desktop Filter Bar */}
        <div style={{ maxWidth: '1600px', margin: '0 auto 2rem auto', padding: '0 2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }} className="filter-bar">
          {user ? (
            <div style={{
              display: 'inline-flex',
              backgroundColor: 'white',
              borderRadius: '30px',
              padding: '6px',
              boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.15)',
              border: '1px solid #d1d5db',
              marginLeft: '1rem'
            }}>
              {['all', 'wanted', 'suggested'].map((val) => (
                <button
                  key={val}
                  onClick={() => {
                    setFilterType(val);
                    if (val === 'suggested' && user) {
                      addDoc(collection(db, 'pageViewEvents'), {
                        pageKey: 'suggested-tab',
                        path: '/latest-pedals',
                        uid: user.uid,
                        createdAt: serverTimestamp(),
                      }).catch(() => {});
                    }
                  }}
                  style={{
                    padding: '0.75rem 2rem',
                    borderRadius: '30px',
                    border: 'none',
                    fontWeight: '700',
                    fontSize: '1.125rem',
                    fontFamily: 'Nunito',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    backgroundColor: filterType === val ? '#C89F3C' : 'transparent',
                    color: filterType === val ? 'white' : '#6B7280'
                  }}
                >
                  {val === 'all' ? 'All' : val === 'wanted' ? 'My Wants' : 'Suggested'}
                </button>
              ))}
            </div>
          ) : <div />}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginRight: '1rem' }}>
            {/* Category filter — desktop dropdown (All / Pedals / Accessories).
                Mirrors the mobile dropdown's gold-bordered styling, scaled up
                to match the adjacent Filter button. */}
            <select
              id="filter-select-desktop"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              style={{
                borderRadius: '25px',
                fontSize: '1.125rem',
                fontFamily: 'Nunito, sans-serif',
                fontWeight: 700,
                backgroundColor: 'white',
                color: '#374151',
                cursor: 'pointer',
                border: '1px solid #C89F3C',
                padding: '0.75rem 2.75rem 0.75rem 1.5rem',
                outline: 'none',
                appearance: 'none',
                WebkitAppearance: 'none',
                MozAppearance: 'none',
                backgroundImage: "url(\"data:image/svg+xml;charset=UTF-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%23C89F3C' d='M6 8L0 0h12z'/%3E%3C/svg%3E\")",
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 1rem center'
              }}
            >
              <option value="all">All</option>
              <option value="pedals">Pedals</option>
              <option value="accessories">Accessories</option>
            </select>
            <button
              onClick={openFilterSheet}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                padding: '0.75rem 1.5rem',
                borderRadius: '25px',
                border: '1px solid #C89F3C',
                backgroundColor: activeFilterCount > 0 ? '#C89F3C' : 'white',
                color: activeFilterCount > 0 ? 'white' : '#374151',
                fontFamily: 'Nunito',
                fontWeight: '700',
                fontSize: '1.125rem',
                cursor: 'pointer',
                whiteSpace: 'nowrap'
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="4" y1="6" x2="20" y2="6"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="10" y1="18" x2="14" y2="18"/>
              </svg>
              Filter{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
            </button>
          </div>
        </div>
        {/* ── Filter sheet (mobile) + panel (desktop) ─────────────────────── */}
        {isFilterSheetOpen && (() => {
          // Shared list: src/utils/effectTypes.js

          const CONDITIONS = ['New', 'Excellent', 'Very Good', 'Good', 'Poor', 'Damaged'];
          const DECADES = ['1950s', '1960s', '1970s', '1980s', '1990s', '2000s', '2010s', '2020s'];
          const CATEGORY_OPTIONS = [
            { value: 'all', label: 'All' },
            { value: 'pedals', label: 'Pedals' },
            { value: 'accessories', label: 'Accessories' },
          ];
          const categoryLabel = CATEGORY_OPTIONS.find(o => o.value === categoryFilter)?.label ?? 'All';

          const chipStyle = (selected) => ({
            padding: '8px 18px',
            borderRadius: '9999px',
            border: selected ? '1.5px solid #C9A227' : '1.5px solid #d1d5db',
            backgroundColor: selected ? 'rgba(201, 162, 39, 0.12)' : 'white',
            color: selected ? '#946F00' : '#374151',
            fontWeight: selected ? '700' : '500',
            fontSize: '0.9rem',
            fontFamily: 'Nunito, sans-serif',
            cursor: 'pointer',
            transition: 'all 0.15s ease'
          });

          const rowStyle = {
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '1rem 1.25rem',
            cursor: 'pointer',
            borderBottom: '1px solid #f3f4f6'
          };

          const badgeCount = (arr) => arr.length > 0
            ? <span style={{ fontSize: '0.85rem', color: '#C89F3C', fontWeight: '700' }}>({arr.length})</span>
            : null;

          const chevronRight = (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2">
              <polyline points="9 18 15 12 9 6"/>
            </svg>
          );
          const chevronLeft = (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="2">
              <polyline points="15 18 9 12 15 6"/>
            </svg>
          );

          const handleFilterTypeChange = (val) => {
            setFilterType(val);
            if (val === 'suggested' && user) {
              addDoc(collection(db, 'pageViewEvents'), {
                pageKey: 'suggested-tab',
                path: '/latest-pedals',
                uid: user.uid,
                createdAt: serverTimestamp(),
              }).catch(() => {});
            }
          };

          const renderMainPage = () => (
            <div style={{ fontFamily: 'Nunito, sans-serif' }}>
              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem 1.25rem', borderBottom: '1px solid #f3f4f6' }}>
                <span style={{ fontSize: '1.1rem', fontWeight: '700', color: '#374151' }}>Filters</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  {activeFilterCount > 0 && (
                    <button
                      onClick={clearAllFilters}
                      style={{ background: 'none', border: 'none', color: '#C89F3C', fontFamily: 'Nunito, sans-serif', fontSize: '0.95rem', fontWeight: '700', cursor: 'pointer', padding: 0 }}
                    >
                      Clear all
                    </button>
                  )}
                  <button onClick={closeFilterSheet} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center' }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  </button>
                </div>
              </div>
              {/* Mobile-only: listing type toggle */}
              <div className="filter-sheet-mobile-extras">
                <style>{`
                  @media (min-width: 769px) { .filter-sheet-mobile-extras { display: none !important; } }
                `}</style>
                {user && (
                  <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid #f3f4f6' }}>
                    <div style={{
                      display: 'inline-flex',
                      backgroundColor: 'white',
                      borderRadius: '25px',
                      padding: '4px',
                      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                      border: '1px solid #e5e7eb',
                      width: '100%',
                      boxSizing: 'border-box',
                      justifyContent: 'center'
                    }}>
                      {['all', 'wanted', 'suggested'].map((val) => (
                        <button
                          key={val}
                          type="button"
                          onClick={() => handleFilterTypeChange(val)}
                          style={{
                            flex: 1,
                            padding: '0.5rem 0.75rem',
                            borderRadius: '25px',
                            border: 'none',
                            fontWeight: '600',
                            fontSize: '0.9rem',
                            fontFamily: 'Nunito, sans-serif',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            backgroundColor: filterType === val ? '#C89F3C' : 'transparent',
                            color: filterType === val ? 'white' : '#6B7280'
                          }}
                        >
                          {val === 'all' ? 'All' : val === 'wanted' ? 'My Wants' : 'Suggested'}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              {/* Price Range */}
              <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid #f3f4f6' }}>
                <p style={{ margin: '0 0 0.75rem 0', fontWeight: '700', fontSize: '0.95rem', color: '#5A9ECB' }}>Price Range</p>
                <div style={{ display: 'flex', gap: '0.75rem', width: '100%', boxSizing: 'border-box', padding: '0 1rem' }}>
                  <input
                    type="number"
                    placeholder="Min $"
                    value={filterPrice.min}
                    onChange={e => setFilterPrice(p => ({ ...p, min: e.target.value }))}
                    style={{ width: '100%', minWidth: 0, boxSizing: 'border-box', padding: '0.4rem 1rem', borderRadius: '25px', border: '1px solid #C89F3C', fontFamily: 'Nunito, sans-serif', fontSize: '0.95rem', outline: 'none' }}
                  />
                  <input
                    type="number"
                    placeholder="Max $"
                    value={filterPrice.max}
                    onChange={e => setFilterPrice(p => ({ ...p, max: e.target.value }))}
                    style={{ width: '100%', minWidth: 0, boxSizing: 'border-box', padding: '0.4rem 1rem', borderRadius: '25px', border: '1px solid #C89F3C', fontFamily: 'Nunito, sans-serif', fontSize: '0.95rem', outline: 'none' }}
                  />
                </div>
              </div>
              {/* Effect Type row */}
              <div style={rowStyle} onClick={() => setFilterSubPage('effectTypes')}>
                <span style={{ fontWeight: '600', fontSize: '0.95rem', color: '#5A9ECB' }}>Effect Type {badgeCount(filterEffectTypes)}</span>
                {chevronRight}
              </div>
              {/* Condition row */}
              <div style={rowStyle} onClick={() => setFilterSubPage('conditions')}>
                <span style={{ fontWeight: '600', fontSize: '0.95rem', color: '#5A9ECB' }}>Condition {badgeCount(filterConditions)}</span>
                {chevronRight}
              </div>
              {/* Decade row */}
              <div style={rowStyle} onClick={() => setFilterSubPage('decades')}>
                <span style={{ fontWeight: '600', fontSize: '0.95rem', color: '#5A9ECB' }}>Decade {badgeCount(filterDecades)}</span>
                {chevronRight}
              </div>
              {/* Offers row (plan §5): Trade / Buy */}
              <div style={rowStyle} onClick={() => setFilterSubPage('offers')}>
                <span style={{ fontWeight: '600', fontSize: '0.95rem', color: '#5A9ECB' }}>Offers {badgeCount(filterOffers)}</span>
                {chevronRight}
              </div>
              {/* Category row — mobile only */}
              <div className="filter-sheet-mobile-extras">
                <div style={{ ...rowStyle, borderBottom: 'none' }} onClick={() => setFilterSubPage('category')}>
                  <span style={{ fontWeight: '600', fontSize: '0.95rem', color: '#5A9ECB' }}>Category</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ fontSize: '0.9rem', color: '#6B7280', fontWeight: '500' }}>{categoryLabel}</span>
                    {chevronRight}
                  </div>
                </div>
              </div>
              {/* Apply button */}
              <div style={{ padding: '1rem 1.25rem', paddingBottom: 'calc(1rem + 100px + env(safe-area-inset-bottom))' }}>
                <button
                  onClick={closeFilterSheet}
                  style={{ width: '100%', padding: '0.75rem', borderRadius: '25px', border: 'none', backgroundColor: '#C89F3C', color: 'white', fontFamily: 'Nunito, sans-serif', fontWeight: '700', fontSize: '1rem', cursor: 'pointer' }}
                >
                  Apply Filters
                </button>
              </div>
            </div>
          );

          const renderSubPage = () => {
            if (filterSubPage === 'category') {
              return (
                <div style={{ fontFamily: 'Nunito, sans-serif' }}>
                  <button onClick={() => setFilterSubPage(null)} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '1rem 1.25rem', borderBottom: '1px solid #f3f4f6', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
                    {chevronLeft}
                    <span style={{ fontSize: '1.1rem', fontWeight: '400', color: '#5A9ECB' }}>Category</span>
                  </button>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', padding: '1rem 1.25rem', justifyContent: 'center' }}>
                    {CATEGORY_OPTIONS.map(opt => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => { setCategoryFilter(opt.value); setFilterSubPage(null); }}
                        style={chipStyle(categoryFilter === opt.value)}
                      >
                        {opt.label}
                      </button>
                    ))}
                    <div style={{ width: '100%', height: 'calc(90px + env(safe-area-inset-bottom))', flexShrink: 0 }} />
                  </div>
                </div>
              );
            }

            const isET = filterSubPage === 'effectTypes';
            const isCond = filterSubPage === 'conditions';
            const isOff = filterSubPage === 'offers';
            const title = isET ? 'Effect Type' : isCond ? 'Condition' : isOff ? 'Offers' : 'Decade';
            const options = isET ? EFFECT_TYPES : isCond ? CONDITIONS : isOff ? OFFER_FILTER_OPTIONS : DECADES;
            const selected = isET ? filterEffectTypes : isCond ? filterConditions : isOff ? filterOffers : filterDecades;
            const setSelected = isET ? setFilterEffectTypes : isCond ? setFilterConditions : isOff ? ((updater) => setFilterOffers((prev) => { const next = typeof updater === 'function' ? updater(prev) : updater; return next.length > 1 ? [next[next.length - 1]] : next; })) : setFilterDecades;
            return (
              <div style={{ fontFamily: 'Nunito, sans-serif' }}>
                <button onClick={() => setFilterSubPage(null)} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '1rem 1.25rem', borderBottom: '1px solid #f3f4f6', background: 'none', border: 'none', borderBottom: '1px solid #f3f4f6', cursor: 'pointer', textAlign: 'left' }}>
                  {chevronLeft}
                  <span style={{ fontSize: '1.1rem', fontWeight: '400', color: '#5A9ECB' }}>{title}</span>
                </button>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', padding: '1rem 1.25rem', justifyContent: 'center' }}>
                  {options.map(opt => {
                    const on = selected.includes(opt);
                    return (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => setSelected(prev => on ? prev.filter(x => x !== opt) : [...prev, opt])}
                        style={chipStyle(on)}
                      >
                        {opt}
                      </button>
                    );
                  })}
                  <div style={{ width: '100%', height: 'calc(90px + env(safe-area-inset-bottom))', flexShrink: 0 }} />
                </div>
              </div>
            );
          };

          const sheetContent = filterSubPage ? renderSubPage() : renderMainPage();

          return (
            <>
              <style>{`
                @keyframes filterSheetFadeIn { from { opacity: 0; } to { opacity: 1; } }
                @keyframes filterSheetFadeOut { from { opacity: 1; } to { opacity: 0; } }
                @keyframes filterSheetSlideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
                @keyframes filterSheetSlideDown { from { transform: translateY(0); } to { transform: translateY(100%); } }
                @keyframes filterPanelSlideIn { from { transform: translateX(-100%); } to { transform: translateX(0); } }
                @keyframes filterPanelSlideOut { from { transform: translateX(0); } to { transform: translateX(-100%); } }
                @media (max-width: 768px) { .filter-panel-desktop { display: none !important; } }
                @media (min-width: 769px) { .filter-sheet-mobile { display: none !important; } }
              `}</style>
              {/* Backdrop (both) */}
              <div
                onClick={closeFilterSheet}
                style={{
                  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                  backgroundColor: 'rgba(0,0,0,0.45)', zIndex: 999,
                  animation: isFilterSheetClosing
                    ? `filterSheetFadeOut ${FILTER_SHEET_ANIM_MS}ms ease-in forwards`
                    : `filterSheetFadeIn 0.18s ease-out`
                }}
              />
              {/* Mobile: slides up from bottom */}
              <div
                className="filter-sheet-mobile"
                role="dialog" aria-modal="true" aria-label="Filter options"
                style={{
                  position: 'fixed', left: 0, right: 0, bottom: 0,
                  backgroundColor: 'white',
                  borderTopLeftRadius: '20px', borderTopRightRadius: '20px',
                  zIndex: 1001, maxHeight: '85vh', overflowY: 'auto',
                  animation: isFilterSheetClosing
                    ? `filterSheetSlideDown ${FILTER_SHEET_ANIM_MS}ms ease-in forwards`
                    : `filterSheetSlideUp ${FILTER_SHEET_ANIM_MS}ms ease-out`
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'center', paddingTop: '10px', paddingBottom: '6px' }}>
                  <div style={{ width: '40px', height: '4px', borderRadius: '2px', backgroundColor: '#D1D5DB' }} />
                </div>
                {sheetContent}
              </div>
              {/* Desktop: slides in from left */}
              <div
                className="filter-panel-desktop"
                role="dialog" aria-modal="true" aria-label="Filter options"
                style={{
                  position: 'fixed', top: 0, left: 0, bottom: 0,
                  width: '320px', backgroundColor: 'white',
                  zIndex: 1001, overflowY: 'auto',
                  animation: isFilterSheetClosing
                    ? `filterPanelSlideOut ${FILTER_SHEET_ANIM_MS}ms ease-in forwards`
                    : `filterPanelSlideIn ${FILTER_SHEET_ANIM_MS}ms ease-out`
                }}
              >
                {sheetContent}
              </div>
            </>
          );
        })()}

        {/* Explainer header for tabs that benefit from context (Suggested,
            My Wants). Hidden on the All tab and on initial loading state. */}
        {!isLoadingFeatured && featuredPedals.length > 0 && (filterType === 'suggested' || filterType === 'wanted') && (
          <div className="latest-pedals-explainer" style={{
            maxWidth: '1100px',
            margin: '0 auto 2.5rem',
            padding: '0 2rem',
            textAlign: 'center',
            fontFamily: 'Nunito, sans-serif'
          }}>
            <style jsx>{`
              @media (max-width: 768px) {
                .latest-pedals-explainer {
                  padding: 0 0.75rem !important;
                  max-width: 100% !important;
                }
                .latest-pedals-explainer p {
                  font-size: 1rem !important;
                  line-height: 1.4 !important;
                }
              }
            `}</style>
            <p style={{
              fontSize: '1.125rem',
              color: '#4B5563',
              lineHeight: 1.5,
              margin: 0
            }}>
              {filterType === 'wanted'
                ? 'Active listings that match the pedals on your Wantlist.'
                : suggestedSource === 'popular_fallback'
                  ? 'Popular pedals on PedalxChange that share an effect type with your Wantlist.'
                  : 'Pedals similar to your Wantlist that other PXC members are also looking for.'}
            </p>
          </div>
        )}

        {filterType === 'wanted' && userWantedPedals.length === 0 && !isLoadingFeatured && (
          <div style={{ textAlign: 'center', padding: '3rem 1.5rem' }}>
            <p style={{ fontSize: '1.1rem', color: '#6B7280', marginBottom: '0.5rem', fontFamily: 'Nunito, sans-serif' }}>
              Your Wantlist is empty.
            </p>
            <p style={{ fontSize: '0.95rem', color: '#9CA3AF', fontFamily: 'Nunito, sans-serif' }}>
              Add pedals to your Wantlist from any listing page or from your My Pedals tab.
            </p>
          </div>
        )}

        <div className="pedalboard-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', columnGap: '2rem', rowGap: '2.5rem', maxWidth: '1600px', margin: '0 auto', padding: '0 2rem' }}>
          <style jsx>{`
            @media (max-width: 1200px) {
              .pedalboard-grid {
                grid-template-columns: repeat(3, 1fr) !important;
              }
            }
            @media (max-width: 900px) {
              .pedalboard-grid {
                grid-template-columns: repeat(2, 1fr) !important;
              }
            }
            @media (max-width: 768px) {
              /* Mobile drops to a single column so cards render at full tile
                 width — matches the Match Opportunities and Wantlist Matches
                 pages. Earlier iteration used a 2-column grid here for higher
                 density, but the new card chrome (image + info pane) reads
                 cramped at half-width on phones. */
              .pedalboard-grid {
                grid-template-columns: 1fr !important;
                max-width: 100% !important;
                padding: 0 1rem !important;
              }
              .latest-search-bar {
                max-width: 100% !important;
                padding-left: 0 !important;
                padding-right: 1.5rem !important;
                box-sizing: border-box !important;
              }
              .filter-bar {
                display: none !important;
              }
              .latest-pedals-grid-section {
                padding-top: 1rem !important;
              }
              .pedalboard-grid {
                row-gap: 2.5rem !important;
              }
              .pedalboard-grid > a {
                display: flex !important;
                flex-direction: column;
              }
              .pedal-card-title {
                flex: 1;
                display: flex !important;
                flex-direction: column;
                justify-content: flex-end;
              }
              .pedal-card-title > div {
                font-size: 1.125rem !important;
              }
              /* Active Trader badge mobile sizing lives in src/index.css so it
                 applies on every page that renders the badge. */
            }
            
            /* Back to Top button positioning - move left on desktop only */
            @media (min-width: 769px) {
              .back-to-top-btn {
                right: 160px !important;
              }
            }
          `}</style>
          {isLoadingFeatured ? (
            Array.from({ length: 28 }).map((_, index) => (
              <div key={index} style={{ 
                borderRadius: '12px', 
                padding: '1.5rem',
                backgroundColor: 'white',
                boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
                animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
              }}>
                <div style={{ marginBottom: '1rem' }}>
                  <div className="h-4 bg-gray-200 rounded mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded mb-1"></div>
                  <div className="h-3 bg-gray-200 rounded mb-1"></div>
                  <div className="h-3 bg-gray-200 rounded"></div>
                </div>
              </div>
            ))
          ) : browseLoadFailed && featuredPedals.length === 0 ? (
            /* FAILED load ≠ empty marketplace (Phase 2a). Until 2026-09-29 a
               double-failed fetch fell through to the "No pedals available"
               copy below, so an offline blip made Browse look empty with no
               recovery short of a refresh. Same pattern as OfferPage's
               load-problem state. Never shown while cards are on screen. */
            <div className="w-full text-center py-12" style={{ gridColumn: '1 / -1' }}>
              <p style={{ fontFamily: 'Nunito, sans-serif', fontSize: '1.05rem', color: '#4A5759', maxWidth: '420px', margin: '0 auto 1.25rem auto', lineHeight: 1.5 }}>
                Couldn&rsquo;t load pedals. Check your connection and try again.
              </p>
              <button
                onClick={() => reloadPage()}
                style={{
                  fontFamily: 'Nunito, sans-serif',
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  color: 'white',
                  backgroundColor: '#4A9FCA',
                  border: 'none',
                  borderRadius: '50px',
                  padding: '0.6rem 2rem',
                  cursor: 'pointer'
                }}
              >
                Try again
              </button>
            </div>
          ) : featuredPedals.length === 0 && !scanPaused ? (
            <div className="w-full text-center py-12" style={{ gridColumn: '1 / -1' }}>
              <p className="text-gray-500" style={{ fontSize: '20px' }}>
                {searchUserWants && searchQuery.trim()
                  ? `No listings found from users who want "${searchQuery}"`
                  : searchQuery.trim()
                    ? `No listings found matching "${searchQuery}"`
                    : filterType === 'wanted' && userWantedPedals.length > 0
                      ? 'Nothing from your Wantlist is listed at the moment.'
                      : filterType === 'suggested'
                        ? 'No suggestions yet.'
                        : categoryFilter === 'accessories'
                          ? 'No accessories available at the moment.'
                          : categoryFilter === 'pedals'
                            ? 'No pedals available at the moment.'
                            : 'No listings available at the moment.'
                }
              </p>
              <p className="text-gray-400 mt-2" style={{ fontSize: '16px' }}>
                {searchUserWants && searchQuery.trim()
                  ? 'No users have this on their Wantlist, or they don\'t have any pedals listed'
                  : searchQuery.trim()
                    ? 'Try a different search term'
                    : filterType === 'wanted' && userWantedPedals.length > 0
                      ? 'Try adding more pedals to your Wantlist or check back later!'
                      : filterType === 'suggested'
                        ? 'Heart a few pedals to build your Wantlist and we\'ll suggest similar ones here, or check back as more listings come in.'
                        : 'Check back later for new listings!'
                }
              </p>
            </div>
          ) : (
            featuredPedals.map((pedal) => (
              <PedalCard key={pedal.id} pedal={pedal} currentUser={user} firestoreUser={firestoreUser} myWants={myWants} onNavigate={saveScrollState} isPotentialMatch={!!user && pedal.ownerId !== user.uid && allowsTrade(pedal) && potentialMatchUserIds.has(pedal.ownerId)} suggestionReason={pedal._reason || null} />
            ))
          )}
        </div>

        {/* Sentinel for infinite scroll — sits just below the grid.
            When this enters the viewport (with rootMargin), the next batch loads. */}
        {!isLoadingFeatured && hasMore && featuredPedals.length > 0 && (
          <div ref={sentinelRef} aria-hidden="true" style={{ height: '1px', width: '100%' }} />
        )}

        {/* Inline "loading more" indicator while auto-load (or manual click) is in flight */}
        {isLoadingMore && (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '2rem', marginBottom: '1rem' }}>
            <p style={{ color: '#6B7280', fontFamily: 'Nunito, sans-serif', fontSize: '15px' }}>
              Loading more pedals…
            </p>
          </div>
        )}

        {/* Manual "Load More" fallback — shown when:
              - auto-load cap is reached, OR
              - browser lacks IntersectionObserver support
            Hidden during normal infinite scroll flow. */}
        {/* Incomplete search, nothing matched yet (2026-10-31): say so, and offer
            to keep scanning. This is NOT the "No listings found" state, which is
            reserved for a scan that reached the end of the marketplace. */}
        {!isLoadingFeatured && !isLoadingMore && hasMore && featuredPedals.length === 0 && scanPaused && (
          <p style={{ textAlign: 'center', fontFamily: 'Nunito, sans-serif', color: '#4A5759', fontSize: '1.05rem', margin: '2rem auto 0', maxWidth: '420px', lineHeight: 1.5 }}>
            Nothing matched in the listings checked so far. The search isn&rsquo;t finished.
          </p>
        )}
        {!isLoadingFeatured && !isLoadingMore && hasMore &&
          ((featuredPedals.length > 0 && (autoLoadCount >= (activeFilterCount > 0 ? Infinity : MAX_AUTO_LOADS) || !SUPPORTS_INTERSECTION_OBSERVER))
            || (featuredPedals.length === 0 && scanPaused)) && (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '3rem', marginBottom: '2rem' }}>
            <button
              onClick={loadMore}
              disabled={isLoadingMore}
              style={{
                backgroundColor: isLoadingMore ? '#9CA3AF' : '#C9A227',
                color: 'white',
                border: 'none',
                borderRadius: '9999px',
                padding: '12px 32px',
                fontFamily: 'Nunito, sans-serif',
                fontWeight: 'bold',
                fontSize: '16px',
                cursor: isLoadingMore ? 'not-allowed' : 'pointer',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                if (!isLoadingMore) {
                  e.target.style.backgroundColor = '#B8912F';
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.15)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isLoadingMore) {
                  e.target.style.backgroundColor = '#C9A227';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
                }
              }}
            >
              {isLoadingMore ? 'Loading...' : (featuredPedals.length === 0 && scanPaused ? 'Keep searching' : 'Load More')}
            </button>
          </div>
        )}
      </div>

      {/* Fixed gap below content */}
      <div style={{ height: '60px' }}></div>
      
      {/* Footer */}
      <div className="border-t border-gray-200 py-4" style={{ backgroundColor: '#F7F3EB', paddingBottom: '10px' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-sm text-gray-600">
              Need help or found a bug?{' '}
              <button 
                onClick={() => openContact({ category: 'support' })}
                className="hover:underline"
                style={{ 
                  color: '#5A9ECB',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  font: 'inherit',
                  cursor: 'pointer'
                }}
              >
                Contact PXC Support
              </button>
            </p>
          </div>
        </div>
      </div>

      {/* Back to Top Button */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className="back-to-top-btn"
          style={{
            position: 'fixed',
            bottom: window.innerWidth <= 768 ? '90px' : '30px',
            right: '30px',
            backgroundColor: '#5A9ECB',
            color: 'white',
            border: 'none',
            borderRadius: '50%',
            width: '36px',
            height: '36px',
            fontSize: '16px',
            fontWeight: 'bold',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            zIndex: 1000,
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: 'system-ui, -apple-system, sans-serif'
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#4A8AB0';
            e.target.style.transform = 'scale(1.1)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = '#5A9ECB';
            e.target.style.transform = 'scale(1)';
          }}
          aria-label="Back to top"
        >
          ⌃
        </button>
      )}

    </div>
  );
};

const PedalCard = ({ pedal, currentUser, firestoreUser, myWants, onNavigate, isPotentialMatch, suggestionReason }) => {
  const navigate = useNavigate();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  const handleProfileClick = (e) => {
    e.stopPropagation();
    navigate(`/profile/${pedal.ownerId}`);
  };

  const handleProfileKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      navigate(`/profile/${pedal.ownerId}`);
    }
  };

  // Build image array in same order as PedalDetails page
  // Real photos, or exactly one placeholder entry for a photo-less listing
  // (src/utils/listingPlaceholder.js).
  const images = listingImageUrls(pedal);
  const showPlaceholderTag = isPlaceholderListing(pedal);

  const handlePrevImage = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNextImage = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  // Membership Profile Badge — overlays the top-left of the owner photo
  // when the seller is a paid member. Same shape/colors as the badge on
  // the Recommendations / Wantlist Matches pages so the visual is
  // consistent. Returns null for free-tier users.
  const getMembershipProfileBadge = (membershipTier) => {
    switch (membershipTier) {
      case 'premium_basic':
      case '9v':
        return (
          <div
            style={{
              position: 'absolute',
              top: '-4px',
              left: '-4px',
              backgroundColor: '#2563eb',
              color: 'white',
              fontWeight: 'bold',
              borderRadius: '4px',
              width: '20px',
              height: '12px',
              fontSize: '0.6rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid #fff',
              boxShadow: '0 0 2px rgba(0,0,0,0.3)'
            }}
          >
            9V
          </div>
        );
      case 'premium_plus':
      case '18v':
        return (
          <div
            style={{
              position: 'absolute',
              top: '-4px',
              left: '-4px',
              backgroundColor: '#9333ea',
              color: 'white',
              fontWeight: 'bold',
              borderRadius: '4px',
              width: '20px',
              height: '12px',
              fontSize: '0.6rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid #fff',
              boxShadow: '0 0 2px rgba(0,0,0,0.3)'
            }}
          >
            18V
          </div>
        );
      default:
        return null;
    }
  };

  // Owner profile lockup (photo + membership badge + trade-count badge),
  // sized to sit in the top-right of the white info pane. Clicking it
  // opens the owner's public profile and stops propagation so the
  // surrounding listing Link doesn't fire. Mirrors the Recommendations /
  // Wantlist Matches pages.
  const ownerLockup = pedal.owner ? (
    <span
      role="link"
      tabIndex={0}
      onClick={handleProfileClick}
      onKeyDown={handleProfileKeyDown}
      aria-label={`View ${pedal.owner.username}'s profile`}
      style={{
        position: 'relative',
        flexShrink: 0,
        display: 'inline-flex',
        textDecoration: 'none',
        lineHeight: 0,
        cursor: 'pointer'
      }}
    >
      <div style={{
        width: '40px',
        height: '40px',
        borderRadius: '50%',
        border: '1px solid #ccc',
        overflow: 'hidden',
        backgroundColor: '#e5e7eb'
      }}>
        {pedal.ownerPhotoURL ? (
          <img
            src={pedal.ownerPhotoURL}
            alt={pedal.owner.username}
            loading="lazy"
            decoding="async"
            style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
          />
        ) : (
          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: '18px' }}>
            👤
          </div>
        )}
      </div>
      {getMembershipProfileBadge(pedal.owner.membershipTier)}
      {pedal.owner.badge !== null && pedal.owner.badge !== undefined && pedal.owner.badge > 0 && (
        <div
          style={{
            position: 'absolute',
            bottom: '-2px',
            right: '-2px',
            backgroundColor: 'gold',
            color: '#000',
            fontWeight: 'bold',
            borderRadius: '50%',
            width: '16px',
            height: '16px',
            fontSize: '0.65rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid #fff',
            boxShadow: '0 0 2px rgba(0,0,0,0.3)'
          }}
        >
          {pedal.owner.badge}
        </div>
      )}
    </span>
  ) : null;

  return (
    <Link
      to={`/listing/${pedal.id}`}
      onClick={onNavigate}
      className="latest-pedal-card"
      style={{
        textDecoration: 'none',
        transition: 'transform 0.2s, box-shadow 0.2s',
        display: 'block',
        backgroundColor: '#ffffff',
        borderRadius: '16px',
        overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.04)',
        border: '1px solid #e5e7eb',
        color: 'inherit'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-4px)';
        e.currentTarget.style.boxShadow = '0 6px 14px rgba(0, 0, 0, 0.10), 0 2px 4px rgba(0, 0, 0, 0.06)';
        setIsHovered(true);
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.04)';
        setIsHovered(false);
      }}
    >
      {/* Image - full width, sits at top of card with carousel and hover overlay */}
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <div className="bg-gray-100 relative" style={{ borderRadius: '15px', overflow: 'hidden', width: '100%', height: 'auto', aspectRatio: '1', padding: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
          {images.length > 0 ? (
            <>
              <ListingThumb
                src={images[currentImageIndex]}
                fallbackSrc={SKETCH_PLACEHOLDER_URL}
                alt={`${pedal.make} ${pedal.model}`}
                loading="lazy"
                style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '15px', display: 'block' }}
                key={currentImageIndex}
              />
              
              {/* Navigation Arrows */}
              <style jsx>{`
                .nav-arrow {
                  position: absolute;
                  top: 50%;
                  transform: translateY(-50%);
                  background: rgba(255, 255, 255, 0.82);
                  border: none;
                  color: #000000;
                  font-size: 26px;
                  cursor: pointer;
                  width: 38px;
                  height: 38px;
                  border-radius: 50%;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  padding: 0;
                  z-index: 2;
                  opacity: 0;
                  transition: opacity 0.2s, background 0.2s;
                  user-select: none;
                }
                
                .nav-arrow-left {
                  left: 6px;
                  padding-bottom: 3px;
                }

                .nav-arrow-right {
                  right: 6px;
                  padding-bottom: 3px;
                }

                .nav-arrow:hover {
                  background: rgba(255, 255, 255, 0.96);
                }
                
                /* Show arrows on hover for desktop */
                @media (hover: hover) and (pointer: fine) {
                  .image-container:hover .nav-arrow {
                    opacity: 1;
                  }
                }
                
                /* Always show arrows on mobile */
                @media (hover: none) or (pointer: coarse) {
                  .nav-arrow {
                    opacity: 0.9;
                  }
                }
                
                /* Description overlay - desktop only.
                   No fixed height: the overlay shrink-wraps to its (already
                   line-clamped) text + padding. This guarantees the visible
                   box matches the number of rendered lines so the last line's
                   ellipsis is never clipped by overflow:hidden.
                   translateY pushes the overlay well clear of the parent's
                   clip region so its top edge can't sliver through at the
                   bottom of the thumbnail due to sub-pixel rounding. */
                .description-overlay {
                  position: absolute;
                  bottom: 8px;
                  left: 8px;
                  right: 8px;
                  background: rgba(31, 41, 55, 0.80);
                  padding: 12px 16px;
                  transform: translateY(calc(100% + 24px));
                  transition: transform 0.25s ease-in-out;
                  display: flex;
                  align-items: flex-start;
                  /* Sit above the lower-left badge stack (z-index: 2) so the
                     description text remains readable on hover. Still below
                     the Active Trader badge (z-index: 10), but that one lives
                     in the top-right and never overlaps the overlay region. */
                  z-index: 3;
                  border-radius: 15px;
                  will-change: transform;
                }
                
                @media (hover: hover) and (pointer: fine) {
                  .image-container:hover .description-overlay {
                    transform: translateY(0);
                  }
                }
                
                @media (hover: none), (pointer: coarse) {
                  .description-overlay {
                    display: none !important;
                  }
                }
                
                .description-text {
                  color: #ffffff;
                  font-size: 16px;
                  font-weight: bold;
                  line-height: 1.4;
                  margin: 0;
                  display: -webkit-box;
                  -webkit-line-clamp: 3;
                  line-clamp: 3;
                  -webkit-box-orient: vertical;
                  overflow: hidden;
                  text-overflow: ellipsis;
                  word-wrap: break-word;
                  overflow-wrap: break-word;
                  /* Hard cap at 3 lines (line-height 1.4 × 3 = 4.2em) so
                     even if -webkit-line-clamp is misapplied the box never
                     bleeds into a 4th visible line. */
                  max-height: calc(1.4em * 3);
                  width: 100%;
                }
                
                @media (max-width: 768px) {
                  /* Only adjust font-size and overlay padding on mobile.
                     Keep line-height: 1.4 and -webkit-line-clamp: 3 from the
                     base rule so the max-height fallback (calc(1.4em * 3))
                     stays in sync — otherwise max-height clips before line 3
                     ends and the ellipsis never renders. */
                  .description-text {
                    font-size: 11px;
                  }
                  .description-overlay {
                    padding: 8px 10px;
                  }
                }
              `}</style>
              
              <div className="image-container" style={{ position: 'absolute', inset: 0 }}>
                {/* Left Arrow */}
                <button
                  className="nav-arrow nav-arrow-left"
                  onClick={handlePrevImage}
                  aria-label="Previous image"
                  style={{ pointerEvents: 'auto' }}
                >
                  ‹
                </button>
                
                {/* Right Arrow */}
                <button
                  className="nav-arrow nav-arrow-right"
                  onClick={handleNextImage}
                  aria-label="Next image"
                  style={{ pointerEvents: 'auto' }}
                >
                  ›
                </button>
                
                {/* Description Overlay - Desktop Only, First Image Only */}
                {pedal.description && currentImageIndex === 0 && (
                  <div className="description-overlay">
                    <p className="description-text">{pedal.description}</p>
                  </div>
                )}
              </div>
              
              {/* Active Trader Badge (if owner is weekly active) */}
              {currentImageIndex === 0 && <ActiveTraderBadge user={pedal.owner} />}
              {currentImageIndex === 0 && showPlaceholderTag && (
                <div
                  title="The owner listed this pedal without photos. Real photos are required before an offer can be accepted."
                  style={{ position: 'absolute', top: '16px', left: '16px', background: 'rgba(255,255,255,0.95)', color: '#4A5759', fontFamily: 'Nunito, sans-serif', fontSize: '13px', fontWeight: 700, letterSpacing: '0.3px', padding: '6px 12px', borderRadius: '9999px', boxShadow: '0 2px 8px rgba(0,0,0,0.2)', zIndex: 10, pointerEvents: 'none' }}
                >
                  Photos coming
                </div>
              )}

              {currentImageIndex === 0 && (
                <PriceDealThumbnailBadge
                  make={pedal.make}
                  model={pedal.model}
                  condition={pedal.condition}
                  price={pedal.price}
                  decade={pedal.decade}
                  listingId={pedal.id}
                />
              )}

              {/* Suggestion "why" tag — small corner badge in the bottom-left.
                  Renders ABOVE the Potential Match banner (when both apply)
                  with extra bottom offset so it clears the banner cleanly.
                  Kept as a corner tag (not full-bleed) because it's
                  informational rather than a primary "this is a match" call-out. */}
              {currentImageIndex === 0 && suggestionReason && (() => {
                const fullText = suggestionReason.type === 'co_want'
                  ? `Similar to ${suggestionReason.bridgeWantLabel}`
                  : 'Popular pick';
                const fullTitle = suggestionReason.type === 'co_want'
                  ? `Similar to ${suggestionReason.bridgeWantLabel}`
                  : `Popular pick: ${suggestionReason.coWanterCount} ${suggestionReason.coWanterCount === 1 ? 'person wants' : 'people want'} ${suggestionReason.coWantedPedalLabel}`;
                // Hard JS truncation guarantees the "…" character actually
                // renders even on narrow mobile tiles where flexbox +
                // overflow:hidden can occasionally swallow CSS ellipsis.
                // 28 chars fits comfortably inside the badge at desktop
                // 11px and mobile 10px without clipping.
                const MAX_CHARS = 28;
                const displayText = fullText.length > MAX_CHARS
                  ? fullText.slice(0, MAX_CHARS - 1).trimEnd() + '…'
                  : fullText;
                // When the Potential Match banner is also showing, push the
                // tag above it via the .has-banner-below modifier class so
                // the offset is responsive (desktop banner ~32px tall, mobile
                // banner ~22px). Offsets live in index.css.
                return (
                  <div
                    className={`suggestion-why-tag${isPotentialMatch ? ' has-banner-below' : ''}`}
                    title={fullTitle}
                    style={{
                      position: 'absolute',
                      bottom: '16px',
                      left: '16px',
                      right: '16px',
                      backgroundColor: 'rgba(90, 158, 203, 0.94)',
                      color: '#ffffff',
                      fontSize: '11px',
                      fontWeight: 700,
                      padding: '4px 8px',
                      borderRadius: '8px',
                      boxShadow: '0 2px 6px rgba(0, 0, 0, 0.2)',
                      fontFamily: 'Nunito, sans-serif',
                      maxWidth: 'fit-content',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      lineHeight: 1.3,
                      zIndex: 2,
                      pointerEvents: 'none'
                    }}
                  >
                    {displayText}
                  </div>
                );
              })()}

              {/* Potential Match — full-width banner pinned to the bottom of
                  the image, mirroring the "Wants your X" treatment on the
                  Recommendations page. Top corners are square so the badge
                  reads as a ribbon flowing out of the image; bottom corners
                  stay rounded to tuck into the card's 16px chrome. The
                  image container's `padding: 8px` means `bottom: 8px / left:
                  8px / right: 8px` are flush with the visible image edges.
                  Sizing matches the dashboard carousel banners — 14px /
                  8px 12px (same as dashboard Potential Match + Wants your X). */}
              {currentImageIndex === 0 && isPotentialMatch && (
                <div
                  className="potential-match-banner"
                  style={{
                    position: 'absolute',
                    bottom: '8px',
                    left: '8px',
                    right: '8px',
                    backgroundColor: 'rgba(46, 125, 50, 0.92)',
                    color: '#ffffff',
                    fontSize: '14px',
                    fontWeight: 700,
                    textAlign: 'center',
                    padding: '8px 12px',
                    borderRadius: '0 0 8px 8px',
                    fontFamily: 'Nunito, sans-serif',
                    zIndex: 2,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    pointerEvents: 'none'
                  }}
                >
                  Potential Match
                </div>
              )}

              {/* Demand heart (bottom-right; Active Trader owns top-right).
                  Lifts above the Potential Match banner when both show. */}
              {currentImageIndex === 0 && (
                <WantDemandBadge
                  pedal={pedal}
                  currentUser={currentUser}
                  firestoreUser={firestoreUser}
                  myWants={myWants}
                  raised={isPotentialMatch}
                />
              )}
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <img
                src={placeholderFor(pedal).url}
                alt="Photos coming"
                className="w-full h-full"
                style={{ objectFit: 'cover', opacity: 0.85 }}
                style={{ borderRadius: '15px !important' }}
              />
            </div>
          )}
        </div>
      </div>
      
      {/* Info section — two-column layout matching Recommendations / Want
          List Matches. Left column (flex: 1): brand (small uppercase) → model
          (bold) → price. Right column (auto): username → owner photo lockup
          on top, condition pill directly below. */}
      <div className="latest-pedal-card-info" style={{ backgroundColor: '#ffffff', padding: '14px 16px 16px', textAlign: 'left' }}>
        <style jsx>{`
          .latest-pedal-card-brand { font-size: 0.8125rem; }
          .latest-pedal-card-model { font-size: 1.25rem; }
          .latest-pedal-card-price { font-size: 1.25rem; }
          .latest-pedal-card-pill { font-size: 0.8125rem; }
          @media (max-width: 768px) {
            .latest-pedal-card-brand { font-size: 1rem; }
            .latest-pedal-card-model { font-size: 1.3125rem; }
            .latest-pedal-card-price { font-size: 1.1875rem; }
            .latest-pedal-card-pill { font-size: 0.75rem; }
          }
        `}</style>
        <div style={{ display: 'flex', alignItems: 'stretch', gap: '12px' }}>
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <div>
              <div title={pedal.make} className="latest-pedal-card-brand" style={{ fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#9ca3af', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginBottom: '2px' }}>
                {pedal.make}
              </div>
              <div title={pedal.model} className="latest-pedal-card-model" style={{ fontWeight: 700, color: '#1f2937', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginBottom: '8px' }}>
                {pedal.model}
              </div>
            </div>
            <PedalPriceDisplay
              price={Number(pedal.price)}
              previousPrice={pedal.previousPrice != null ? Number(pedal.previousPrice) : null}
              layout="standalone"
              className="latest-pedal-card-price"
            offerMode={offerModeOf(pedal)}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'space-between', gap: '6px', flexShrink: 0, minWidth: 0, maxWidth: '40%' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px', minWidth: 0, maxWidth: '100%' }}>
              {/* Username — sits above the photo lockup so users can match
                  the listing to a specific trader at a glance without
                  leaving the card. Ellipsis on overflow; aria-hidden because
                  the lockup itself is already labeled with the username. */}
              {pedal.owner?.username && (
                <div
                  title={pedal.owner.username}
                  aria-hidden="true"
                  className="latest-pedal-card-username"
                  style={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: '#6b7280',
                    lineHeight: 1.2,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: '100%'
                  }}
                >
                  {pedal.owner.username}
                </div>
              )}
              {ownerLockup}
            </div>
            <span className="latest-pedal-card-pill" style={{ display: 'inline-flex', alignItems: 'center', padding: '4px 10px', borderRadius: '9999px', backgroundColor: '#F3F4F6', color: '#374151', fontWeight: 700, whiteSpace: 'nowrap' }}>
              {pedal.condition}
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default LatestPedals;
